import React, { useState, useEffect, useCallback, useRef } from 'react'
import { usePrintStore, type PrintMethod } from '@renderer/stores/printStore'
import { useConfigStore } from '@renderer/stores/configState'
import { renderFrameTestImage } from '@renderer/utils/frameTestImage'
import Button from '@renderer/components/Button'
import { useTranslation } from 'react-i18next'
import { FaUpload, FaPrint, FaDownload } from 'react-icons/fa'
import { SettingsDisclosure } from './SettingsDisclosure'
import { useDeviceManager, useDesignLayout, useSessionLayout } from '@renderer/hooks'
import cogoToast from '@successtar/cogo-toast'
import { PrintUtils, buildPrintOptions, PrinterCaps } from '@renderer/utils/printUtils'
import {
  DREAMBOOTH_RECOMMENDED_PRESET_ID,
  findPrinterRecommendation,
  getRecommendationDpi,
  getRecommendationMarginType,
  isStockAppPrintSettings,
  mergeRecommendationIntoSettings,
  presetMatchesRecommendation,
  type PrinterRecommendation
} from '@renderer/utils/printerRecommendations'
import {
  capabilities,
  getHostAppVersion,
  getPreloadApi,
  getTunnelStatus,
  hasElectronPreload,
} from '@renderer/lib/runtime'
import { isInTauriShell } from '@renderer/lib/runtime'
import { logPrintEvent } from '@renderer/utils/printEventLogger'
import { FrameSelectionModal } from './FrameSelectionModal'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useHubStore } from '@renderer/stores/hubStore'
import { PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS } from '@renderer/utils/printResultMinDisplay'

interface PrinterSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  isElectron?: boolean
  /**
   * `modal` (default) is the standalone dialog. `panel` renders the same editor
   * with no backdrop and no card, to be hosted directly inside the Settings pane —
   * see the auto-commit note by `saveAllTabs`.
   */
  presentation?: 'modal' | 'panel'
  /** Panel only: rendered at the top of the controls column (the pane's enable/picker block). */
  leading?: React.ReactNode
  /** Panel only: controls beside the preview, rather than stacked. Measured by the host. */
  twoColumn?: boolean
  /** Panel only: helpers that belong with the preview (rendered under the print actions). */
  aside?: React.ReactNode
  /**
   * Panel only: the default slot's picker, rendered in the sticky header beside the
   * tabs. The pane owns it because it is the one picker that also lists remote
   * printers.
   */
  slotPicker?: React.ReactNode
}

interface MarginSettings {
  top: number
  bottom: number
  left: number
  right: number
}

type PrinterTab = 'default' | 'secondary' | 'tertiary'

export const PrinterSettingsModal: React.FC<PrinterSettingsModalProps> = ({
  isOpen,
  onClose,
  isElectron = hasElectronPreload(),
  presentation = 'modal',
  slotPicker,
  leading,
  twoColumn = false,
  aside
}) => {
  const isPanel = presentation === 'panel'
  /**
   * Panel field grammar — the same row the Booth page uses: one plain title, one
   * grey hint under it, one compact control on the right. Each field is a small
   * grid so the stacked markup (label, control, hint in DOM order) lays out as a
   * row without restructuring; the standalone modal keeps its stacked classes.
   */
  const fieldClass = isPanel ? 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 py-3' : ''
  const fieldTitleClass = isPanel
    ? 'col-start-1 row-start-1 text-sm text-gray-800'
    : 'block text-sm font-medium mb-2 text-gray-700'
  const fieldHintClass = isPanel
    ? 'col-start-1 row-start-2 mt-0.5 text-xs text-gray-500'
    : 'text-xs text-gray-500 mt-1'
  const controlPos = 'col-start-2 row-start-1 row-span-2 justify-self-end'
  const printerToggleClass =
    "w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
  const inputBase = 'rounded-md border border-gray-300 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
  const selectClass = isPanel
    ? `${controlPos} w-64 max-w-full p-2 ${inputBase}`
    : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const numberClass = isPanel
    ? `w-24 p-1.5 text-center ${inputBase}`
    : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const soloNumberClass = isPanel ? `${controlPos} ${numberClass}` : numberClass
  /** Two labelled numbers on the right (page size, DPI). */
  const pairClass = isPanel ? `${controlPos} grid grid-cols-2 gap-x-3 gap-y-1` : 'grid grid-cols-2 gap-4'
  /** Four labelled numbers under the title (margins, position). */
  const quadClass = isPanel ? 'col-span-2 mt-2 grid grid-cols-2 gap-x-6 gap-y-2' : 'grid grid-cols-2 gap-4'
  const pairCellClass = isPanel ? 'flex items-center gap-1.5' : ''
  const pairLabelClass = isPanel ? 'text-xs text-gray-500' : 'block text-xs text-gray-500 mb-1'
  const isNativeShell = isElectron || isInTauriShell()
  const { t } = useTranslation()
  const { isCompact: isViewportCompact } = useSessionLayout()
  const { isCompact: printerDesignCompact, isPortrait: printerDesignPortrait } = useDesignLayout()
  const printerSheetDense = (capabilities.isMobile || isViewportCompact) && printerDesignCompact && printerDesignPortrait
  const {
    settings,
    updateSettings,
    secondaryPrinter,
    setSecondaryPrinter,
    tertiaryPrinter,
    setTertiaryPrinter,
    selectedPrinter: printStoreSelectedPrinter,
    setSelectedPrinter: updateGlobalSelectedPrinter,
    androidPrinterIp,
    setAndroidPrinterIp
  } = usePrintStore()
  const printResultMinDisplaySeconds = useSettingsStore((s) => s.printResultMinDisplaySeconds)
  const setPrintResultMinDisplaySeconds = useSettingsStore((s) => s.setPrintResultMinDisplaySeconds)
  const { frames, getFrame } = useConfigStore()
  const { devices } = useDeviceManager()
  const [activeTab, setActiveTab] = useState<PrinterTab>('default')
  const [showAdvancedTier, setShowAdvancedTier] = useState<Record<PrinterTab, boolean>>({
    default: false,
    secondary: false,
    tertiary: false
  })
  const [tempSettings, setTempSettings] = useState(settings)
  const [tempSecondarySettings, setTempSecondarySettings] = useState({
    ...settings,
    ...(secondaryPrinter?.settings || {}),
    orientation: secondaryPrinter?.settings?.orientation || 'landscape',
    pageSize: {
      height: secondaryPrinter?.settings?.pageSize?.height || 105000,
      width: secondaryPrinter?.settings?.pageSize?.width || 156100
    },
    scaleFactor: secondaryPrinter?.settings?.scaleFactor || 100
  })
  const [tempTertiarySettings, setTempTertiarySettings] = useState({
    ...settings,
    ...(tertiaryPrinter?.settings || {}),
    orientation: tertiaryPrinter?.settings?.orientation || 'landscape',
    pageSize: {
      height: tertiaryPrinter?.settings?.pageSize?.height || 105000,
      width: tertiaryPrinter?.settings?.pageSize?.width || 156100
    },
    scaleFactor: tertiaryPrinter?.settings?.scaleFactor || 100
  })
  const [marginType, setMarginType] = useState<'default' | 'none' | 'printableArea' | 'custom'>(
    settings.margins?.marginType || 'default'
  )
  const [secondaryMarginType, setSecondaryMarginType] = useState<
    'default' | 'none' | 'printableArea' | 'custom'
  >(secondaryPrinter?.settings?.margins?.marginType || 'default')
  const [tertiaryMarginType, setTertiaryMarginType] = useState<
    'default' | 'none' | 'printableArea' | 'custom'
  >(tertiaryPrinter?.settings?.margins?.marginType || 'default')
  const [margins, setMargins] = useState<MarginSettings>({
    top: settings.margins?.top || 0,
    bottom: settings.margins?.bottom || 0,
    left: settings.margins?.left || 0,
    right: settings.margins?.right || 0
  })
  const [secondaryMargins, setSecondaryMargins] = useState<MarginSettings>({
    top: secondaryPrinter?.settings?.margins?.top || 0,
    bottom: secondaryPrinter?.settings?.margins?.bottom || 0,
    left: secondaryPrinter?.settings?.margins?.left || 0,
    right: secondaryPrinter?.settings?.margins?.right || 0
  })
  const [tertiaryMargins, setTertiaryMargins] = useState<MarginSettings>({
    top: tertiaryPrinter?.settings?.margins?.top || 0,
    bottom: tertiaryPrinter?.settings?.margins?.bottom || 0,
    left: tertiaryPrinter?.settings?.margins?.left || 0,
    right: tertiaryPrinter?.settings?.margins?.right || 0
  })
  const [positionShift, setPositionShift] = useState({
    top: settings.printPositionShift?.top || 0,
    bottom: settings.printPositionShift?.bottom ?? -0.5,
    left: settings.printPositionShift?.left || 0,
    right: settings.printPositionShift?.right || 0
  })
  const [secondaryPositionShift, setSecondaryPositionShift] = useState({
    top: secondaryPrinter?.settings?.printPositionShift?.top || 0,
    bottom: secondaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
    left: secondaryPrinter?.settings?.printPositionShift?.left || 0,
    right: secondaryPrinter?.settings?.printPositionShift?.right || 0
  })
  const [tertiaryPositionShift, setTertiaryPositionShift] = useState({
    top: tertiaryPrinter?.settings?.printPositionShift?.top || 0,
    bottom: tertiaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
    left: tertiaryPrinter?.settings?.printPositionShift?.left || 0,
    right: tertiaryPrinter?.settings?.printPositionShift?.right || 0
  })
  const [dpi, setDpi] = useState({
    horizontal: settings.dpi?.horizontal || 600,
    vertical: settings.dpi?.vertical || 600
  })
  const [secondaryDpi, setSecondaryDpi] = useState({
    horizontal: secondaryPrinter?.settings?.dpi?.horizontal || 600,
    vertical: secondaryPrinter?.settings?.dpi?.vertical || 600
  })
  const [tertiaryDpi, setTertiaryDpi] = useState({
    horizontal: tertiaryPrinter?.settings?.dpi?.horizontal || 600,
    vertical: tertiaryPrinter?.settings?.dpi?.vertical || 600
  })
  const [scaleFactor, setScaleFactor] = useState(settings.scaleFactor || 100)
  const [secondaryScaleFactor, setSecondaryScaleFactor] = useState(
    secondaryPrinter?.settings?.scaleFactor || 100
  )
  const [tertiaryScaleFactor, setTertiaryScaleFactor] = useState(
    tertiaryPrinter?.settings?.scaleFactor || 100
  )
  const [printBackground, setPrintBackground] = useState(settings.printBackground ?? true)
  const [secondaryPrintBackground, setSecondaryPrintBackground] = useState(
    secondaryPrinter?.settings?.printBackground ?? true
  )
  const [tertiaryPrintBackground, setTertiaryPrintBackground] = useState(
    tertiaryPrinter?.settings?.printBackground ?? true
  )
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [selectedPrinter, setSelectedPrinter] = useState<string>(printStoreSelectedPrinter || '')
  const [selectedSecondaryPrinter, setSelectedSecondaryPrinter] = useState<string>(
    secondaryPrinter?.printerName || ''
  )
  const [selectedTertiaryPrinter, setSelectedTertiaryPrinter] = useState<string>(
    tertiaryPrinter?.printerName || ''
  )
  const [copies, setCopies] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [isFrameSelectionOpen, setIsFrameSelectionOpen] = useState(false)
  const [frameSelectionPrinterType, setFrameSelectionPrinterType] = useState<
    'default' | 'secondary' | 'tertiary'
  >('secondary')
  const [selectedFrameId, setSelectedFrameId] = useState<string>('')
  const [printResolutionScale, setPrintResolutionScale] = useState(
    settings.printResolutionScale || 1
  )
  // Refs to store previous margin and DPI values when SumatraPDF mode is enabled
  const prevMarginTypeRef = useRef<'default' | 'none' | 'printableArea' | 'custom'>('default')
  const prevSecondaryMarginTypeRef = useRef<'default' | 'none' | 'printableArea' | 'custom'>(
    'default'
  )
  const prevTertiaryMarginTypeRef = useRef<'default' | 'none' | 'printableArea' | 'custom'>(
    'default'
  )
  const prevDpiRef = useRef<{ horizontal: number; vertical: number }>({
    horizontal: 600,
    vertical: 600
  })
  const prevSecondaryDpiRef = useRef<{ horizontal: number; vertical: number }>({
    horizontal: 600,
    vertical: 600
  })
  const prevTertiaryDpiRef = useRef<{ horizontal: number; vertical: number }>({
    horizontal: 600,
    vertical: 600
  })
  const [secondaryPrintResolutionScale, setSecondaryPrintResolutionScale] = useState(
    secondaryPrinter?.settings?.printResolutionScale || 1
  )
  const [tertiaryPrintResolutionScale, setTertiaryPrintResolutionScale] = useState(
    tertiaryPrinter?.settings?.printResolutionScale || 1
  )
  const [tunnelUrl, setTunnelUrl] = useState<string | null>(null)
  const [isTunnelActive, setIsTunnelActive] = useState<boolean>(false)
  const [printerCapabilities, setPrinterCapabilities] = useState<PrinterCaps | null>(null)
  const [secondaryPrinterCapabilities, setSecondaryPrinterCapabilities] =
    useState<PrinterCaps | null>(null)
  const [tertiaryPrinterCapabilities, setTertiaryPrinterCapabilities] =
    useState<PrinterCaps | null>(null)
  const [loadingCapabilities, setLoadingCapabilities] = useState<boolean>(false)

  // Check if printer capabilities API is available (for backward compatibility)
  const [hasPrinterCapsSupport, setHasPrinterCapsSupport] = useState<boolean>(false)
  const [appVersion, setAppVersion] = useState<string | null>(null)

  // Combined preset dropdown state
  const [selectedPresetId, setSelectedPresetId] = useState<string>('custom')
  const [selectedSecondaryPresetId, setSelectedSecondaryPresetId] = useState<string>('custom')
  const [selectedTertiaryPresetId, setSelectedTertiaryPresetId] = useState<string>('custom')
  const [secondaryPurpose, setSecondaryPurpose] = useState<'special-frames' | 'parallel'>(
    secondaryPrinter?.purpose || 'special-frames'
  )
  const [tertiaryPurpose, setTertiaryPurpose] = useState<'special-frames' | 'parallel'>(
    tertiaryPrinter?.purpose || 'special-frames'
  )
  const [combinedOptions, setCombinedOptions] = useState<
    Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }>
  >([])
  const [secondaryCombinedOptions, setSecondaryCombinedOptions] = useState<
    Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }>
  >([])
  const [tertiaryCombinedOptions, setTertiaryCombinedOptions] = useState<
    Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }>
  >([])

  // Advanced settings visibility
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false)
  const [showSecondaryAdvancedSettings, setShowSecondaryAdvancedSettings] = useState<boolean>(false)
  const [showTertiaryAdvancedSettings, setShowTertiaryAdvancedSettings] = useState<boolean>(false)
  // A custom preset is edited in the Details block. In the standalone modal the
  // operator has always had to open it themselves; inline, that hides the very
  // fields they just asked for.
  useEffect(() => {
    if (presentation !== 'panel') return
    if (selectedPresetId === 'custom') setShowAdvancedSettings(true)
    if (selectedSecondaryPresetId === 'custom') setShowSecondaryAdvancedSettings(true)
    if (selectedTertiaryPresetId === 'custom') setShowTertiaryAdvancedSettings(true)
  }, [presentation, selectedPresetId, selectedSecondaryPresetId, selectedTertiaryPresetId])
  // Position shift visibility
  const [showPositionShift, setShowPositionShift] = useState<boolean>(false)
  const [showSecondaryPositionShift, setShowSecondaryPositionShift] = useState<boolean>(false)
  const [showTertiaryPositionShift, setShowTertiaryPositionShift] = useState<boolean>(false)

  // C# printing toggle
  const {
    useCSharpPrinting,
    setUseCSharpPrinting,
    setCustomSettingsMemory,
    getCustomSettingsMemory,
    useSumatraPDF,
    secondaryUseSumatraPDF,
    tertiaryUseSumatraPDF,
    // Print method + hot-folder config (subscribe to raw fields so the UI re-renders)
    printMethod,
    secondaryPrintMethod,
    tertiaryPrintMethod,
    hotFolderConfig,
    secondaryHotFolderConfig,
    tertiaryHotFolderConfig,
    setPrintMethod,
    setHotFolderConfig,
    getPrintMethod,
    defaultRouting,
    setDefaultRouting,
    setSlotCategories,
    activeProjectId,
    printerSlotByProject,
    setPrinterSlotForProject
  } = usePrintStore()
  // Multi-project hub: "use this printer for the active project" binding.
  // A hub project never prints, so it is never bindable.
  const activeProjectTitle = useConfigStore((s) => s.config?.title)
  const activeProjectIsHub = useConfigStore((s) =>
    Boolean(s.config?.pages?.modeSelect?.settings?.enabled)
  )
  // Phase 2 routing UI (Default-tab picker, category claims) is flag-gated —
  // see docs/printer-frame-routing-plan.md.
  const multiClaimRouting = useFeatureFlagsStore((s) => s.getFlag('printer_multi_claim_routing'))
  /**
   * Routing is a multi-printer, multi-project concern. "Use this printer for the
   * active project" only means something inside a hub (a single-project booth has
   * nothing to bind), and frame routing only once a second slot exists. On the
   * booth most operators run — one printer, one project — both rows were pure noise.
   */
  const inHub = useHubStore((s) => Boolean(s.hubId))
  const hasAdditionalPrinters = Boolean(secondaryPrinter?.printerName || tertiaryPrinter?.printerName)

  const applyRecommendationToModalSlot = useCallback(
    (
      recommendation: PrinterRecommendation,
      printerType: 'default' | 'secondary' | 'tertiary'
    ): void => {
      const baseSettings =
        printerType === 'secondary'
          ? tempSecondarySettings
          : printerType === 'tertiary'
            ? tempTertiarySettings
            : tempSettings
      const merged = mergeRecommendationIntoSettings(baseSettings, recommendation)
      const margin = getRecommendationMarginType(recommendation)
      const recDpi = getRecommendationDpi(recommendation)

      if (printerType === 'secondary') {
        setTempSecondarySettings({
          ...merged,
          scaleFactor: merged.scaleFactor ?? 100
        })
        setSecondaryDpi(recDpi)
        setSecondaryMarginType(margin)
        setSecondaryScaleFactor(merged.scaleFactor ?? 100)
        setSecondaryPrintBackground(merged.printBackground ?? true)
        setSecondaryPrintResolutionScale(merged.printResolutionScale ?? 1)
        setSelectedSecondaryPresetId(DREAMBOOTH_RECOMMENDED_PRESET_ID)
      } else if (printerType === 'tertiary') {
        setTempTertiarySettings({
          ...merged,
          scaleFactor: merged.scaleFactor ?? 100
        })
        setTertiaryDpi(recDpi)
        setTertiaryMarginType(margin)
        setTertiaryScaleFactor(merged.scaleFactor ?? 100)
        setTertiaryPrintBackground(merged.printBackground ?? true)
        setTertiaryPrintResolutionScale(merged.printResolutionScale ?? 1)
        setSelectedTertiaryPresetId(DREAMBOOTH_RECOMMENDED_PRESET_ID)
      } else {
        setTempSettings(merged)
        setDpi(recDpi)
        setMarginType(margin)
        setScaleFactor(merged.scaleFactor ?? 100)
        setPrintBackground(merged.printBackground ?? true)
        setPrintResolutionScale(merged.printResolutionScale ?? 1)
        setSelectedPresetId(DREAMBOOTH_RECOMMENDED_PRESET_ID)
      }

      setPrintMethod(recommendation.printMethod, printerType)
    },
    [
      tempSecondarySettings,
      tempTertiarySettings,
      tempSettings,
      setPrintMethod
    ]
  )

  const tryAutoApplyRecommendation = useCallback(
    (printerName: string, printerType: 'default' | 'secondary' | 'tertiary'): void => {
      const recommendation = findPrinterRecommendation(printerName)
      if (!recommendation?.autoApply) return

      const slotSettings =
        printerType === 'secondary'
          ? tempSecondarySettings
          : printerType === 'tertiary'
            ? tempTertiarySettings
            : tempSettings

      if (!isStockAppPrintSettings(slotSettings, getPrintMethod(printerType))) return

      applyRecommendationToModalSlot(recommendation, printerType)
    },
    [
      applyRecommendationToModalSlot,
      getPrintMethod,
      tempSecondarySettings,
      tempTertiarySettings,
      tempSettings
    ]
  )

  // The preview is the frame as it prints (its artwork and empty photo
  // windows), not its thumbnail: a thumbnail is only a picture for pickers and
  // can differ from the print (BK's 6x8 sheets show a single strip). The
  // thumbnail stands in only until the render lands.
  useEffect(() => {
    const frameId = selectedFrameId || frames[0]?._id
    if (!frameId) return
    if (!selectedFrameId) setSelectedFrameId(frameId)
    const frame = getFrame(frameId)
    if (!frame) return
    let alive = true
    if (frame.thumbnail) setPreviewImage(frame.thumbnail)
    renderFrameTestImage(frame.drawParams)
      .then((image) => {
        if (alive) setPreviewImage(image)
      })
      .catch((error) => console.warn('[PrinterSettings] frame preview render failed:', error))
    return (): void => {
      alive = false
    }
  }, [frames, getFrame, selectedFrameId])

  // Reset temp settings when modal opens (to ensure fresh state from store)
  // Use a ref to track if this is the first render after opening
  const prevIsOpenRef = useRef<boolean>(false)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      setTempSettings(settings)
      setTempSecondarySettings({
        ...settings,
        ...(secondaryPrinter?.settings || {}),
        orientation: secondaryPrinter?.settings?.orientation || 'landscape',
        pageSize: {
          height: secondaryPrinter?.settings?.pageSize?.height || 105000,
          width: secondaryPrinter?.settings?.pageSize?.width || 156100
        },
        scaleFactor: secondaryPrinter?.settings?.scaleFactor || 100
      })
      setTempTertiarySettings({
        ...settings,
        ...(tertiaryPrinter?.settings || {}),
        orientation: tertiaryPrinter?.settings?.orientation || 'landscape',
        pageSize: {
          height: tertiaryPrinter?.settings?.pageSize?.height || 105000,
          width: tertiaryPrinter?.settings?.pageSize?.width || 156100
        },
        scaleFactor: tertiaryPrinter?.settings?.scaleFactor || 100
      })
      // Reset other temp states
      setMarginType(settings.margins?.marginType || 'default')
      setSecondaryMarginType(secondaryPrinter?.settings?.margins?.marginType || 'default')
      setTertiaryMarginType(tertiaryPrinter?.settings?.margins?.marginType || 'default')
      setMargins({
        top: settings.margins?.top || 0,
        bottom: settings.margins?.bottom || 0,
        left: settings.margins?.left || 0,
        right: settings.margins?.right || 0
      })
      setSecondaryMargins({
        top: secondaryPrinter?.settings?.margins?.top || 0,
        bottom: secondaryPrinter?.settings?.margins?.bottom || 0,
        left: secondaryPrinter?.settings?.margins?.left || 0,
        right: secondaryPrinter?.settings?.margins?.right || 0
      })
      setTertiaryMargins({
        top: tertiaryPrinter?.settings?.margins?.top || 0,
        bottom: tertiaryPrinter?.settings?.margins?.bottom || 0,
        left: tertiaryPrinter?.settings?.margins?.left || 0,
        right: tertiaryPrinter?.settings?.margins?.right || 0
      })
      setDpi({
        horizontal: settings.dpi?.horizontal || 600,
        vertical: settings.dpi?.vertical || 600
      })
      setSecondaryDpi({
        horizontal: secondaryPrinter?.settings?.dpi?.horizontal || 600,
        vertical: secondaryPrinter?.settings?.dpi?.vertical || 600
      })
      setTertiaryDpi({
        horizontal: tertiaryPrinter?.settings?.dpi?.horizontal || 600,
        vertical: tertiaryPrinter?.settings?.dpi?.vertical || 600
      })
      setScaleFactor(settings.scaleFactor || 100)
      setSecondaryScaleFactor(secondaryPrinter?.settings?.scaleFactor || 100)
      setTertiaryScaleFactor(tertiaryPrinter?.settings?.scaleFactor || 100)
      setPrintBackground(settings.printBackground ?? true)
      setSecondaryPrintBackground(secondaryPrinter?.settings?.printBackground ?? true)
      setTertiaryPrintBackground(tertiaryPrinter?.settings?.printBackground ?? true)
      setPrintResolutionScale(settings.printResolutionScale || 1)
      setSecondaryPrintResolutionScale(secondaryPrinter?.settings?.printResolutionScale || 1)
      setTertiaryPrintResolutionScale(tertiaryPrinter?.settings?.printResolutionScale || 1)
      setPositionShift({
        top: settings.printPositionShift?.top || 0,
        bottom: settings.printPositionShift?.bottom ?? -0.5,
        left: settings.printPositionShift?.left || 0,
        right: settings.printPositionShift?.right || 0
      })
      setSecondaryPositionShift({
        top: secondaryPrinter?.settings?.printPositionShift?.top || 0,
        bottom: secondaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
        left: secondaryPrinter?.settings?.printPositionShift?.left || 0,
        right: secondaryPrinter?.settings?.printPositionShift?.right || 0
      })
      setTertiaryPositionShift({
        top: tertiaryPrinter?.settings?.printPositionShift?.top || 0,
        bottom: tertiaryPrinter?.settings?.printPositionShift?.bottom ?? -0.5,
        left: tertiaryPrinter?.settings?.printPositionShift?.left || 0,
        right: tertiaryPrinter?.settings?.printPositionShift?.right || 0
      })
      setSelectedPrinter(printStoreSelectedPrinter || '')
      setSelectedSecondaryPrinter(secondaryPrinter?.printerName || '')
      setSelectedTertiaryPrinter(tertiaryPrinter?.printerName || '')
      setSecondaryPurpose(secondaryPrinter?.purpose || 'special-frames')
      setTertiaryPurpose(tertiaryPrinter?.purpose || 'special-frames')
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, settings, secondaryPrinter, tertiaryPrinter, printStoreSelectedPrinter])

  // Sync selected printer with store when it changes
  useEffect(() => {
    setSelectedPrinter(printStoreSelectedPrinter || '')
  }, [printStoreSelectedPrinter])

  // Clear/reset margins and DPI when SumatraPDF mode is enabled
  useEffect(() => {
    if (useSumatraPDF) {
      // Store previous values before resetting
      prevMarginTypeRef.current = marginType
      prevDpiRef.current = dpi
      // Reset to defaults
      setMarginType('default')
      setDpi({ horizontal: 600, vertical: 600 })
    } else {
      // Restore previous values if they exist
      if (prevMarginTypeRef.current) {
        setMarginType(prevMarginTypeRef.current)
      }
      if (prevDpiRef.current) {
        setDpi(prevDpiRef.current)
      }
    }
  }, [useSumatraPDF])

  // Clear/reset margins and DPI for secondary printer when SumatraPDF mode is enabled
  useEffect(() => {
    if (secondaryUseSumatraPDF) {
      // Store previous values before resetting
      prevSecondaryMarginTypeRef.current = secondaryMarginType
      prevSecondaryDpiRef.current = secondaryDpi
      // Reset to defaults
      setSecondaryMarginType('default')
      setSecondaryDpi({ horizontal: 600, vertical: 600 })
    } else {
      // Restore previous values if they exist
      if (prevSecondaryMarginTypeRef.current) {
        setSecondaryMarginType(prevSecondaryMarginTypeRef.current)
      }
      if (prevSecondaryDpiRef.current) {
        setSecondaryDpi(prevSecondaryDpiRef.current)
      }
    }
  }, [secondaryUseSumatraPDF])

  // Clear/reset margins and DPI for tertiary printer when SumatraPDF mode is enabled
  useEffect(() => {
    if (tertiaryUseSumatraPDF) {
      // Store previous values before resetting
      prevTertiaryMarginTypeRef.current = tertiaryMarginType
      prevTertiaryDpiRef.current = tertiaryDpi
      // Reset to defaults
      setTertiaryMarginType('default')
      setTertiaryDpi({ horizontal: 600, vertical: 600 })
    } else {
      // Restore previous values if they exist
      if (prevTertiaryMarginTypeRef.current) {
        setTertiaryMarginType(prevTertiaryMarginTypeRef.current)
      }
      if (prevTertiaryDpiRef.current) {
        setTertiaryDpi(prevTertiaryDpiRef.current)
      }
    }
  }, [tertiaryUseSumatraPDF])

  // Check printer capabilities support on mount
  useEffect(() => {
    // Check if the getPrinterCapabilities API is available
    const api = getPreloadApi()
    const hasSupport = isElectron && typeof api?.getPrinterCapabilities === 'function'
    setHasPrinterCapsSupport(hasSupport)

    if (!hasSupport && isElectron) {
      console.warn(
        'Printer capabilities API not available. Using legacy printer settings mode. Please update your Electron app for smart printer presets.'
      )
    }

    // Fetch app version for SumatraPDF feature availability check
    if (isElectron) {
      void getHostAppVersion()
        .then((version) => setAppVersion(version ?? null))
        .catch((error) => {
          console.error('Failed to get app version:', error)
          setAppVersion(null)
        })
    }
  }, [isElectron])

  // Fetch tunnel status and load capabilities when modal opens
  useEffect(() => {
    if (isOpen && isElectron) {
      const fetchTunnelStatus = async (): Promise<void> => {
        try {
          const status = await getTunnelStatus()
          setTunnelUrl(status.url)
          setIsTunnelActive(status.isActive)
        } catch (error) {
          console.error('Failed to fetch tunnel status:', error)
          setTunnelUrl(null)
          setIsTunnelActive(false)
        }
      }
      fetchTunnelStatus()

      // Load capabilities for currently selected printers (only if supported)
      if (hasPrinterCapsSupport) {
        if (selectedPrinter) {
          loadPrinterCapabilities(selectedPrinter, 'default')
        }
        if (selectedSecondaryPrinter) {
          loadPrinterCapabilities(selectedSecondaryPrinter, 'secondary')
        }
        if (selectedTertiaryPrinter) {
          loadPrinterCapabilities(selectedTertiaryPrinter, 'tertiary')
        }
      }
    }
  }, [
    isOpen,
    isElectron,
    selectedPrinter,
    selectedSecondaryPrinter,
    selectedTertiaryPrinter,
    hasPrinterCapsSupport
  ])

  // Generate combined options from capabilities (default printer)
  useEffect(() => {
    if (!printerCapabilities) {
      setCombinedOptions([])
      setShowAdvancedSettings(true)
      return
    }

    const options: Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }> = []

    const pageSizes = printerCapabilities.capabilities.pageSizes || []
    const dpis = printerCapabilities.capabilities.dpis || []

    // If we have both pageSizes and dpis, generate all combinations
    if (pageSizes.length > 0 && dpis.length > 0) {
      pageSizes.forEach((size, sizeIdx) => {
        dpis.forEach((dpi) => {
          (['landscape', 'portrait'] as const).forEach((orientation) => {
            options.push({
              id: `preset-${sizeIdx}-${dpi.x}-${dpi.y}-${orientation}`,
              name: size.name ?? null,
              orientation,
              pageSize: { width: size.widthMicrons, height: size.heightMicrons },
              dpi: { horizontal: dpi.x, vertical: dpi.y }
            })
          })
        })
      })
      setShowAdvancedSettings(false)
    }
    // If we have pageSizes but no dpis, use default dpi
    else if (pageSizes.length > 0) {
      const defaultDpi = printerCapabilities.defaults.dpi || { x: 300, y: 300 }
      pageSizes.forEach((size, sizeIdx) => {
        (['landscape', 'portrait'] as const).forEach((orientation) => {
          options.push({
            id: `preset-${sizeIdx}-${defaultDpi.x}-${defaultDpi.y}-${orientation}`,
            name: size.name ?? null,
            orientation,
            pageSize: { width: size.widthMicrons, height: size.heightMicrons },
            dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
          })
        })
      })
      setShowAdvancedSettings(false)
    }
    // If no capabilities but have defaults, create one option from defaults
    else if (printerCapabilities.defaults.pageSize || printerCapabilities.defaults.dpi) {
      const defaultPageSize = printerCapabilities.defaults.pageSize || {
        name: null,
        widthMicrons: 156100,
        heightMicrons: 105000
      }
      const defaultDpi = printerCapabilities.defaults.dpi || { x: 300, y: 300 }

      options.push({
        id: 'preset-default-landscape',
        name: defaultPageSize.name ?? '#1',
        orientation: 'landscape',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      options.push({
        id: 'preset-default-portrait',
        name: defaultPageSize.name ?? '#1',
        orientation: 'portrait',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      setShowAdvancedSettings(true) // Show advanced since we only have defaults
    } else {
      setShowAdvancedSettings(true)
    }

    setCombinedOptions(options)
  }, [printerCapabilities])

  // Generate combined options from capabilities (secondary printer)
  useEffect(() => {
    if (!secondaryPrinterCapabilities) {
      setSecondaryCombinedOptions([])
      setShowSecondaryAdvancedSettings(true)
      return
    }

    const options: Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }> = []

    const pageSizes = secondaryPrinterCapabilities.capabilities.pageSizes || []
    const dpis = secondaryPrinterCapabilities.capabilities.dpis || []

    if (pageSizes.length > 0 && dpis.length > 0) {
      pageSizes.forEach((size, sizeIdx) => {
        dpis.forEach((dpi) => {
          (['landscape', 'portrait'] as const).forEach((orientation) => {
            options.push({
              id: `preset-${sizeIdx}-${dpi.x}-${dpi.y}-${orientation}`,
              name: size.name ?? null,
              orientation,
              pageSize: { width: size.widthMicrons, height: size.heightMicrons },
              dpi: { horizontal: dpi.x, vertical: dpi.y }
            })
          })
        })
      })
      setShowSecondaryAdvancedSettings(false)
    } else if (pageSizes.length > 0) {
      const defaultDpi = secondaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }
      pageSizes.forEach((size, sizeIdx) => {
        (['landscape', 'portrait'] as const).forEach((orientation) => {
          options.push({
            id: `preset-${sizeIdx}-${defaultDpi.x}-${defaultDpi.y}-${orientation}`,
            name: size.name ?? null,
            orientation,
            pageSize: { width: size.widthMicrons, height: size.heightMicrons },
            dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
          })
        })
      })
      setShowSecondaryAdvancedSettings(false)
    } else if (
      secondaryPrinterCapabilities.defaults.pageSize ||
      secondaryPrinterCapabilities.defaults.dpi
    ) {
      const defaultPageSize = secondaryPrinterCapabilities.defaults.pageSize || {
        name: null,
        widthMicrons: 156100,
        heightMicrons: 105000
      }
      const defaultDpi = secondaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }

      options.push({
        id: 'preset-default-landscape',
        name: defaultPageSize.name ?? '#1',
        orientation: 'landscape',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      options.push({
        id: 'preset-default-portrait',
        name: defaultPageSize.name ?? '#1',
        orientation: 'portrait',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      setShowSecondaryAdvancedSettings(true)
    } else {
      setShowSecondaryAdvancedSettings(true)
    }

    setSecondaryCombinedOptions(options)
  }, [secondaryPrinterCapabilities])

  // Generate combined options from capabilities (tertiary printer)
  useEffect(() => {
    if (!tertiaryPrinterCapabilities) {
      setTertiaryCombinedOptions([])
      setShowTertiaryAdvancedSettings(true)
      return
    }

    const options: Array<{
      id: string
      name: string | null
      orientation: 'landscape' | 'portrait'
      pageSize: { width: number; height: number }
      dpi: { horizontal: number; vertical: number }
    }> = []

    const pageSizes = tertiaryPrinterCapabilities.capabilities.pageSizes || []
    const dpis = tertiaryPrinterCapabilities.capabilities.dpis || []

    if (pageSizes.length > 0 && dpis.length > 0) {
      pageSizes.forEach((size, sizeIdx) => {
        dpis.forEach((dpi) => {
          (['landscape', 'portrait'] as const).forEach((orientation) => {
            options.push({
              id: `preset-${sizeIdx}-${dpi.x}-${dpi.y}-${orientation}`,
              name: size.name ?? null,
              orientation,
              pageSize: { width: size.widthMicrons, height: size.heightMicrons },
              dpi: { horizontal: dpi.x, vertical: dpi.y }
            })
          })
        })
      })
      setShowTertiaryAdvancedSettings(false)
    } else if (pageSizes.length > 0) {
      const defaultDpi = tertiaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }
      pageSizes.forEach((size, sizeIdx) => {
        (['landscape', 'portrait'] as const).forEach((orientation) => {
          options.push({
            id: `preset-${sizeIdx}-${defaultDpi.x}-${defaultDpi.y}-${orientation}`,
            name: size.name ?? null,
            orientation,
            pageSize: { width: size.widthMicrons, height: size.heightMicrons },
            dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
          })
        })
      })
      setShowTertiaryAdvancedSettings(false)
    } else if (
      tertiaryPrinterCapabilities.defaults.pageSize ||
      tertiaryPrinterCapabilities.defaults.dpi
    ) {
      const defaultPageSize = tertiaryPrinterCapabilities.defaults.pageSize || {
        name: null,
        widthMicrons: 156100,
        heightMicrons: 105000
      }
      const defaultDpi = tertiaryPrinterCapabilities.defaults.dpi || { x: 300, y: 300 }

      options.push({
        id: 'preset-default-landscape',
        name: defaultPageSize.name ?? '#1',
        orientation: 'landscape',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      options.push({
        id: 'preset-default-portrait',
        name: defaultPageSize.name ?? '#1',
        orientation: 'portrait',
        pageSize: {
          width: defaultPageSize.widthMicrons,
          height: defaultPageSize.heightMicrons
        },
        dpi: { horizontal: defaultDpi.x, vertical: defaultDpi.y }
      })

      setShowTertiaryAdvancedSettings(true)
    } else {
      setShowTertiaryAdvancedSettings(true)
    }

    setTertiaryCombinedOptions(options)
  }, [tertiaryPrinterCapabilities])

  // Load printer capabilities
  const loadPrinterCapabilities = async (
    printerName: string,
    printerType: 'default' | 'secondary' | 'tertiary' = 'default'
  ): Promise<void> => {
    if (!printerName || !isElectron) return

    setLoadingCapabilities(true)
    try {
      const capabilities = await PrintUtils.getPrinterCapabilities(printerName)
      if (printerType === 'secondary') {
        setSecondaryPrinterCapabilities(capabilities)
      } else if (printerType === 'tertiary') {
        setTertiaryPrinterCapabilities(capabilities)
      } else {
        setPrinterCapabilities(capabilities)
      }
    } catch (error) {
      console.error('Failed to load printer capabilities:', error)
    } finally {
      setLoadingCapabilities(false)
    }
  }

  // Handle preset selection from combined dropdown
  const handlePresetChange = (
    presetId: string,
    printerType: 'default' | 'secondary' | 'tertiary' = 'default'
  ): void => {
    const currentSettings =
      printerType === 'secondary'
        ? tempSecondarySettings
        : printerType === 'tertiary'
          ? tempTertiarySettings
          : tempSettings
    const currentDpi =
      printerType === 'secondary' ? secondaryDpi : printerType === 'tertiary' ? tertiaryDpi : dpi
    const currentPresetId =
      printerType === 'secondary'
        ? selectedSecondaryPresetId
        : printerType === 'tertiary'
          ? selectedTertiaryPresetId
          : selectedPresetId

    // If switching FROM custom to a preset, save current custom settings
    if (currentPresetId === 'custom' && presetId !== 'custom') {
      setCustomSettingsMemory(
        {
          orientation: currentSettings.orientation,
          pageSize: currentSettings.pageSize,
          dpi: currentDpi
        },
        printerType
      )
    }

    if (printerType === 'secondary') {
      setSelectedSecondaryPresetId(presetId)
    } else if (printerType === 'tertiary') {
      setSelectedTertiaryPresetId(presetId)
    } else {
      setSelectedPresetId(presetId)
    }

    if (presetId === DREAMBOOTH_RECOMMENDED_PRESET_ID) {
      const printerName =
        printerType === 'secondary'
          ? selectedSecondaryPrinter
          : printerType === 'tertiary'
            ? selectedTertiaryPrinter
            : selectedPrinter
      const recommendation = printerName ? findPrinterRecommendation(printerName) : null
      if (recommendation) {
        applyRecommendationToModalSlot(recommendation, printerType)
      }
      return
    }

    if (presetId === 'custom') {
      // Restore saved custom settings if available
      const savedCustom = getCustomSettingsMemory(printerType)
      if (savedCustom) {
        if (printerType === 'secondary') {
          setTempSecondarySettings((prev) => ({
            ...prev,
            orientation: savedCustom.orientation,
            pageSize: savedCustom.pageSize
          }))
          setSecondaryDpi(savedCustom.dpi)
        } else if (printerType === 'tertiary') {
          setTempTertiarySettings((prev) => ({
            ...prev,
            orientation: savedCustom.orientation,
            pageSize: savedCustom.pageSize
          }))
          setTertiaryDpi(savedCustom.dpi)
        } else {
          setTempSettings((prev) => ({
            ...prev,
            orientation: savedCustom.orientation,
            pageSize: savedCustom.pageSize
          }))
          setDpi(savedCustom.dpi)
        }
      }
      return
    }

    const options =
      printerType === 'secondary'
        ? secondaryCombinedOptions
        : printerType === 'tertiary'
          ? tertiaryCombinedOptions
          : combinedOptions
    const preset = options.find((opt) => opt.id === presetId)
    if (!preset) return

    // Update all settings based on preset
    if (printerType === 'secondary') {
      setTempSecondarySettings((prev) => ({
        ...prev,
        orientation: preset.orientation,
        pageSize: preset.pageSize
      }))
      setSecondaryDpi(preset.dpi)
    } else if (printerType === 'tertiary') {
      setTempTertiarySettings((prev) => ({
        ...prev,
        orientation: preset.orientation,
        pageSize: preset.pageSize
      }))
      setTertiaryDpi(preset.dpi)
    } else {
      setTempSettings((prev) => ({
        ...prev,
        orientation: preset.orientation,
        pageSize: preset.pageSize
      }))
      setDpi(preset.dpi)
    }
  }

  const convertToBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = (): void => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('Error converting image to base64:', error)
      return url // Fallback to original URL if conversion fails
    }
  }

  const handleFileUpload = useCallback((file: File): void => {
    const reader = new FileReader()
    reader.onload = (event): void => {
      const imageUrl = event.target?.result as string
      setPreviewImage(imageUrl)
    }
    reader.readAsDataURL(file)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        handleFileUpload(file)
      }
    },
    [handleFileUpload]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  // Save all tabs' settings to store
  const saveAllTabs = (): void => {
    // Save default printer settings
    const newSettings = {
      ...tempSettings,
      orientation: tempSettings.orientation,
      pageSize: tempSettings.pageSize,
      margins: {
        marginType,
        ...(marginType === 'custom' ? margins : {})
      },
      dpi,
      scaleFactor,
      printBackground,
      printResolutionScale,
      printPositionShift: positionShift
    }
    updateSettings(newSettings)

    // Save secondary printer settings
    setSecondaryPrinter({
      printerName: selectedSecondaryPrinter || '',
      settings: {
        ...tempSecondarySettings,
        orientation: tempSecondarySettings.orientation,
        pageSize: tempSecondarySettings.pageSize,
        margins: {
          marginType: secondaryMarginType,
          ...(secondaryMarginType === 'custom' ? secondaryMargins : {})
        },
        dpi: secondaryDpi,
        scaleFactor: secondaryScaleFactor,
        printBackground: secondaryPrintBackground,
        printResolutionScale: secondaryPrintResolutionScale,
        printPositionShift: secondaryPositionShift
      },
      frameIds: secondaryPrinter?.frameIds ? [...secondaryPrinter.frameIds] : [],
      purpose: secondaryPurpose
    })

    // Save tertiary printer settings
    setTertiaryPrinter({
      printerName: selectedTertiaryPrinter || '',
      settings: {
        ...tempTertiarySettings,
        orientation: tempTertiarySettings.orientation,
        pageSize: tempTertiarySettings.pageSize,
        margins: {
          marginType: tertiaryMarginType,
          ...(tertiaryMarginType === 'custom' ? tertiaryMargins : {})
        },
        dpi: tertiaryDpi,
        scaleFactor: tertiaryScaleFactor,
        printBackground: tertiaryPrintBackground,
        printResolutionScale: tertiaryPrintResolutionScale,
        printPositionShift: tertiaryPositionShift
      },
      frameIds: tertiaryPrinter?.frameIds ? [...tertiaryPrinter.frameIds] : [],
      purpose: tertiaryPurpose
    })
  }

  /**
   * Panel presentation lives inside the Settings pane, where every other control —
   * enable printing, screensaver, auto-launch — writes straight to the store the
   * moment you touch it. A Save/Cancel island in the middle of that is the odd one
   * out, and the print preview sitting beside these controls already shows the
   * result live, so a Save button confirms nothing the operator cannot already see.
   *
   * So: commit on change, debounced, so dragging a scale slider does not hammer the
   * store. This cannot loop — the store->temp reset above is gated on the
   * closed->open transition and never fires on a store write while open.
   */
  const saveAllTabsRef = useRef(saveAllTabs)
  saveAllTabsRef.current = saveAllTabs
  const committedSnapshot = JSON.stringify([
    tempSettings, marginType, margins, dpi, scaleFactor, printBackground,
    printResolutionScale, positionShift,
    selectedSecondaryPrinter, tempSecondarySettings, secondaryMarginType, secondaryMargins,
    secondaryDpi, secondaryScaleFactor, secondaryPrintBackground,
    secondaryPrintResolutionScale, secondaryPositionShift, secondaryPurpose,
    selectedTertiaryPrinter, tempTertiarySettings, tertiaryMarginType, tertiaryMargins,
    tertiaryDpi, tertiaryScaleFactor, tertiaryPrintBackground,
    tertiaryPrintResolutionScale, tertiaryPositionShift, tertiaryPurpose
  ])
  const skipFirstCommitRef = useRef(true)
  useEffect(() => {
    if (!isPanel) return
    // The first pass is the values we just read OUT of the store; writing them
    // straight back would be a no-op churn on every mount.
    if (skipFirstCommitRef.current) {
      skipFirstCommitRef.current = false
      return
    }
    const id = setTimeout(() => saveAllTabsRef.current(), 250)
    return (): void => clearTimeout(id)
  }, [isPanel, committedSnapshot])
  // The page unmounts when the rail switches; a change made inside the debounce
  // window must still land.
  useEffect(() => {
    if (!isPanel) return
    return (): void => saveAllTabsRef.current()
  }, [isPanel])

  const handlePreview = async (): Promise<void> => {
    if (!previewImage) return

    // The test print is the frame itself, rendered at the print resolution a
    // real session uses — never its thumbnail, which can differ from the
    // print. Falls back to the preview image only if that render fails.
    const scaleForPrint =
      activeTab === 'default'
        ? printResolutionScale
        : activeTab === 'secondary'
          ? secondaryPrintResolutionScale
          : tertiaryPrintResolutionScale
    const frameForPrint = selectedFrameId ? getFrame(selectedFrameId, scaleForPrint || 1) : undefined
    const rendered = frameForPrint
      ? await renderFrameTestImage(frameForPrint.drawParams).catch((error) => {
          console.warn('[PrinterSettings] test print render failed, using preview:', error)
          return null
        })
      : null
    const base64Image =
      rendered ??
      (previewImage.startsWith('data:') ? previewImage : await convertToBase64(previewImage))

    const currentPrinter =
      activeTab === 'default'
        ? selectedPrinter
        : activeTab === 'secondary'
          ? selectedSecondaryPrinter
          : selectedTertiaryPrinter

    try {
      const currentSettings =
        activeTab === 'default'
          ? tempSettings
          : activeTab === 'secondary'
            ? tempSecondarySettings
            : tempTertiarySettings
      const currentMargins =
        activeTab === 'default'
          ? margins
          : activeTab === 'secondary'
            ? secondaryMargins
            : tertiaryMargins
      const currentMarginType =
        activeTab === 'default'
          ? marginType
          : activeTab === 'secondary'
            ? secondaryMarginType
            : tertiaryMarginType
      const currentDpi =
        activeTab === 'default' ? dpi : activeTab === 'secondary' ? secondaryDpi : tertiaryDpi
      const currentScaleFactor =
        activeTab === 'default'
          ? scaleFactor
          : activeTab === 'secondary'
            ? secondaryScaleFactor
            : tertiaryScaleFactor
      const currentPrintBackground =
        activeTab === 'default'
          ? printBackground
          : activeTab === 'secondary'
            ? secondaryPrintBackground
            : tertiaryPrintBackground
      const currentPrintResolutionScale =
        activeTab === 'default'
          ? printResolutionScale
          : activeTab === 'secondary'
            ? secondaryPrintResolutionScale
            : tertiaryPrintResolutionScale
      const currentPositionShift =
        activeTab === 'default'
          ? positionShift
          : activeTab === 'secondary'
            ? secondaryPositionShift
            : tertiaryPositionShift

      // Validate printer selection
      if (!currentPrinter && hasElectronPreload()) {
        cogoToast.error(
          t('printerSettings.feedback.selectPrinter')
        )
        return
      }

      // Log settings for debugging
      console.log('Print settings:', {
        printer: currentPrinter,
        settings: {
          orientation: currentSettings.orientation,
          pageSize: currentSettings.pageSize,
          margins: {
            marginType: currentMarginType,
            ...(currentMarginType === 'custom' ? currentMargins : {})
          },
          dpi: currentDpi,
          scaleFactor: currentScaleFactor,
          printBackground: currentPrintBackground,
          printResolutionScale: currentPrintResolutionScale
        }
      })

      // Built from the SAME helper a live session uses, so a passing test print
      // means the session path will send byte-identical options. The values come
      // from the modal's unsaved state on purpose — that is what "test before you
      // save" means — but the option SET must not differ, or a green test print
      // stops being evidence about live printing.
      const result = await PrintUtils.sendToPrinter(
        base64Image,
        buildPrintOptions(
          currentPrinter || 'DS-RX1',
          {
            orientation: currentSettings.orientation,
            pageSize: currentSettings.pageSize,
            margins: {
              marginType: currentMarginType,
              ...(currentMarginType === 'custom' ? currentMargins : {})
            },
            dpi: currentDpi,
            scaleFactor: currentScaleFactor,
            printBackground: currentPrintBackground,
            printResolutionScale: currentPrintResolutionScale,
            printPositionShift: currentPositionShift
          },
          { copies: isNativeShell ? copies : 1, silent: true }
        )
      )

      if (result.success) {
        cogoToast.success(t('printerSettings.feedback.printInitiated'))
        logPrintEvent({ eventType: 'test_print', copies: isNativeShell ? copies : 1, printerName: currentPrinter || 'DS-RX1', printerType: result.printerType, success: true })
      } else {
        console.error('Print failed:', result.error)
        const errorMsg = result.failureReason || result.error || 'Unknown error'
        void logPrintEvent({ eventType: 'test_print', copies: isNativeShell ? copies : 1, printerName: currentPrinter || 'DS-RX1', printerType: result.printerType, success: false, error: errorMsg })
        cogoToast.error(`${t('printerSettings.feedback.printFailed')}: ${errorMsg}`)
      }
    } catch (error) {
      console.error('Print error:', error)
      const errMsg = error instanceof Error ? error.message : 'Unknown error'
      void logPrintEvent({ eventType: 'test_print', copies: isNativeShell ? copies : 1, printerName: currentPrinter || 'DS-RX1', success: false, error: errMsg })
      cogoToast.error(t('printerSettings.feedback.printFailed'))
    }
  }

  if (!isOpen) return null

  const renderPrinterSettings = (
    printerType: 'default' | 'secondary' | 'tertiary' = 'default',
    part: 'controls' | 'picker' = 'controls'
  ): JSX.Element => {
    const isSecondary = printerType === 'secondary'
    const isTertiary = printerType === 'tertiary'
    const currentSettings = isSecondary
      ? tempSecondarySettings
      : isTertiary
        ? tempTertiarySettings
        : tempSettings
    const currentMargins = isSecondary ? secondaryMargins : isTertiary ? tertiaryMargins : margins
    const currentMarginType = isSecondary
      ? secondaryMarginType
      : isTertiary
        ? tertiaryMarginType
        : marginType
    const currentPositionShift = isSecondary
      ? secondaryPositionShift
      : isTertiary
        ? tertiaryPositionShift
        : positionShift
    const currentDpi = isSecondary ? secondaryDpi : isTertiary ? tertiaryDpi : dpi
    const currentScaleFactor = isSecondary
      ? secondaryScaleFactor
      : isTertiary
        ? tertiaryScaleFactor
        : scaleFactor
    const currentPrintBackground = isSecondary
      ? secondaryPrintBackground
      : isTertiary
        ? tertiaryPrintBackground
        : printBackground
    const currentPrintResolutionScale = isSecondary
      ? secondaryPrintResolutionScale
      : isTertiary
        ? tertiaryPrintResolutionScale
        : printResolutionScale
    const currentPrinter = isSecondary
      ? selectedSecondaryPrinter
      : isTertiary
        ? selectedTertiaryPrinter
        : selectedPrinter
    const setCurrentPrinter = isSecondary
      ? setSelectedSecondaryPrinter
      : isTertiary
        ? setSelectedTertiaryPrinter
        : setSelectedPrinter
    const setcurrentPrintResolutionScale = isSecondary
      ? setSecondaryPrintResolutionScale
      : isTertiary
        ? setTertiaryPrintResolutionScale
        : setPrintResolutionScale
    const currentOptions = isSecondary
      ? secondaryCombinedOptions
      : isTertiary
        ? tertiaryCombinedOptions
        : combinedOptions
    const currentPresetId = isSecondary
      ? selectedSecondaryPresetId
      : isTertiary
        ? selectedTertiaryPresetId
        : selectedPresetId
    const currentShowAdvanced = isSecondary
      ? showSecondaryAdvancedSettings
      : isTertiary
        ? showTertiaryAdvancedSettings
        : showAdvancedSettings
    const setCurrentShowAdvanced = isSecondary
      ? setShowSecondaryAdvancedSettings
      : isTertiary
        ? setShowTertiaryAdvancedSettings
        : setShowAdvancedSettings

    // SumatraPDF settings
    const currentUseSumatraPDF = isSecondary
      ? secondaryUseSumatraPDF
      : isTertiary
        ? tertiaryUseSumatraPDF
        : useSumatraPDF
    // Print method + hot-folder config for the active printer slot.
    const methodSlot: 'default' | 'secondary' | 'tertiary' = isSecondary
      ? 'secondary'
      : isTertiary
        ? 'tertiary'
        : 'default'
    const rawPrintMethod = isSecondary
      ? secondaryPrintMethod
      : isTertiary
        ? tertiaryPrintMethod
        : printMethod
    // Derive method, falling back to the legacy SumatraPDF boolean when unset.
    const currentPrintMethod: PrintMethod =
      rawPrintMethod ?? (currentUseSumatraPDF ? 'sumatra' : 'electron')
    const currentHotFolderConfig = isSecondary
      ? secondaryHotFolderConfig
      : isTertiary
        ? tertiaryHotFolderConfig
        : hotFolderConfig
    const isHotFolder = currentPrintMethod === 'hotFolder'

    // Contextual help shown under the print-method selector for the chosen method.
    const printMethodHelp: Record<PrintMethod, string> = {
      electron: t(
        'printerSettings.printMethod.help.appPrint',
        'Recommended. The app controls the size, layout and quality, then sends the print. Best for standard sizes (4×6, strips, 5×7).'
      ),
      sumatra: t(
        'printerSettings.printMethod.help.driver',
        "Sends to the printer using the driver's own default settings for margins and DPI. Better compatibility with some custom printer drivers."
      ),
      hotFolder: t(
        'printerSettings.printMethod.help.hotFolder',
        'Drops each print into a watched folder monitored by external print software (e.g. DNP Hot Folder Print). Use this for output sizes the normal driver can’t produce, such as panoramic (6×14 / 6×20).'
      )
    }

    // Check if version supports SumatraPDF (>= 2.0.2)
    const supportsSumatraPDF = ((): boolean => {
      if (!isElectron || !appVersion) return false

      const versionParts = appVersion.split('.').map(Number)
      const minVersionParts = [2, 0, 2].map(Number)

      for (let i = 0; i < Math.max(versionParts.length, minVersionParts.length); i++) {
        const versionPart = versionParts[i] || 0
        const minPart = minVersionParts[i] || 0

        if (versionPart > minPart) return true
        if (versionPart < minPart) return false
      }

      return true // Versions are equal
    })()

    // Hot Folder support is feature-detected from the bundled main process, not
    // version-gated: the renderer is web-served and reaches every booth instantly,
    // but the file-writing only exists in updated .exe builds. Still show the
    // option if it's already the saved method (so the value isn't silently lost).
    const supportsHotFolder =
      (isElectron && getPreloadApi()?.hotFolderSupported === true) || isHotFolder

    /** The slot's printer picker: the pane's remote-capable one for the default slot
        in the panel, the slot's own select otherwise, plus the frame-routing button. */
    const slotPickerRow = (
          <div className="flex gap-2">
            {isPanel && printerType === 'default' ? (
              <div className="min-w-0 flex-1">{slotPicker}</div>
            ) : (
            <select
              value={currentPrinter || 'system-default'}
              onChange={(e) => {
                const selectedValue = e.target.value === 'system-default' ? '' : e.target.value
                setCurrentPrinter(selectedValue)
                if (printerType === 'default') {
                  updateGlobalSelectedPrinter(selectedValue)
                }
                if (selectedValue) {
                  loadPrinterCapabilities(selectedValue, printerType)
                  tryAutoApplyRecommendation(selectedValue, printerType)
                }
              }}
              className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              aria-label={t('settings.printer.selectPrinter')}
            >
              <option value="system-default">
                {t('settings.printer.systemDefault')}
              </option>
              {devices?.printers.map((printer) => (
                <option key={printer} value={printer}>
                  {printer}
                </option>
              ))}
            </select>
            )}
            {(isSecondary || isTertiary) &&
              (printerType === 'secondary'
                ? secondaryPurpose === 'special-frames'
                : tertiaryPurpose === 'special-frames') && (
                <Button
                  text={t('printerSettings.selectFrames')}
                  variant="secondary"
                  onClick={() => {
                    setFrameSelectionPrinterType(
                      printerType === 'secondary' ? 'secondary' : 'tertiary'
                    )
                    setIsFrameSelectionOpen(true)
                  }}
                  className="!px-2 !py-0 !text-lg"
                />
              )}
            {!isSecondary && !isTertiary && multiClaimRouting && hasAdditionalPrinters && (
              <Button
                text={t('printerSettings.selectFrames')}
                variant="secondary"
                onClick={() => {
                  setFrameSelectionPrinterType('default')
                  setIsFrameSelectionOpen(true)
                }}
                className="!px-2 !py-0 !text-lg"
              />
            )}
          </div>
    )
    if (part === 'picker') return slotPickerRow

    /** The manual overrides behind a preset (orientation, page size, DPI). */
    const presetDetailsBody = (
                <div className={isPanel ? 'divide-y divide-gray-100' : 'space-y-4'}>
                  {/* Manual Orientation */}
                  <div className={fieldClass}>
                    <label className={fieldTitleClass}>
                      {t('printerSettings.orientation')}
                    </label>
                    <select
                      title={t('printerSettings.orientation')}
                      value={currentSettings.orientation}
                      onChange={(e) => {
                        if (isSecondary) {
                          setTempSecondarySettings((prev) => ({
                            ...prev,
                            orientation: e.target.value as 'landscape' | 'portrait'
                          }))
                          setSelectedSecondaryPresetId('custom')
                        } else if (isTertiary) {
                          setTempTertiarySettings((prev) => ({
                            ...prev,
                            orientation: e.target.value as 'landscape' | 'portrait'
                          }))
                          setSelectedTertiaryPresetId('custom')
                        } else {
                          setTempSettings((prev) => ({
                            ...prev,
                            orientation: e.target.value as 'landscape' | 'portrait'
                          }))
                          setSelectedPresetId('custom')
                        }
                      }}
                      className={selectClass}
                    >
                      <option value="landscape">
                        {t('printerSettings.landscape')}
                      </option>
                      <option value="portrait">
                        {t('printerSettings.portrait')}
                      </option>
                    </select>
                  </div>

                  {/* Manual Page Size */}
                  <div className={fieldClass}>
                    <label className={fieldTitleClass}>
                      {t('printerSettings.pageSize')}
                    </label>
                    <div className={pairClass}>
                      <div className={pairCellClass}>
                        <label className={pairLabelClass}>
                          {t('printerSettings.width')}
                        </label>
                        <input
                          type="number"
                          value={currentSettings.pageSize.width}
                          onChange={(e) => {
                            if (isSecondary) {
                              setTempSecondarySettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  width: parseInt(e.target.value)
                                }
                              }))
                              setSelectedSecondaryPresetId('custom')
                            } else if (isTertiary) {
                              setTempTertiarySettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  width: parseInt(e.target.value)
                                }
                              }))
                              setSelectedTertiaryPresetId('custom')
                            } else {
                              setTempSettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  width: parseInt(e.target.value)
                                }
                              }))
                              setSelectedPresetId('custom')
                            }
                          }}
                          title={t('printerSettings.width')}
                          className={numberClass}
                        />
                      </div>
                      <div className={pairCellClass}>
                        <label className={pairLabelClass}>
                          {t('printerSettings.height')}
                        </label>
                        <input
                          type="number"
                          value={currentSettings.pageSize.height}
                          onChange={(e) => {
                            if (isSecondary) {
                              setTempSecondarySettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  height: parseInt(e.target.value)
                                }
                              }))
                              setSelectedSecondaryPresetId('custom')
                            } else if (isTertiary) {
                              setTempTertiarySettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  height: parseInt(e.target.value)
                                }
                              }))
                              setSelectedTertiaryPresetId('custom')
                            } else {
                              setTempSettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  height: parseInt(e.target.value)
                                }
                              }))
                              setSelectedPresetId('custom')
                            }
                          }}
                          title={t('printerSettings.height')}
                          className={numberClass}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Manual DPI */}
                  <div className={fieldClass}>
                    <label className={fieldTitleClass}>
                      {t('printerSettings.dpi')}
                    </label>
                    <div className={pairClass}>
                      <div className={pairCellClass}>
                        <label className={pairLabelClass}>
                          {t('printerSettings.dpi.horizontal')}
                        </label>
                        <input
                          type="number"
                          value={currentDpi.horizontal}
                          onChange={(e) => {
                            if (isSecondary) {
                              setSecondaryDpi((prev) => ({
                                ...prev,
                                horizontal: parseInt(e.target.value)
                              }))
                              setSelectedSecondaryPresetId('custom')
                            } else if (isTertiary) {
                              setTertiaryDpi((prev) => ({
                                ...prev,
                                horizontal: parseInt(e.target.value)
                              }))
                              setSelectedTertiaryPresetId('custom')
                            } else {
                              setDpi((prev) => ({
                                ...prev,
                                horizontal: parseInt(e.target.value)
                              }))
                              setSelectedPresetId('custom')
                            }
                          }}
                          title={t('printerSettings.dpi.horizontal')}
                          className={numberClass}
                        />
                      </div>
                      <div className={pairCellClass}>
                        <label className={pairLabelClass}>
                          {t('printerSettings.dpi.vertical')}
                        </label>
                        <input
                          type="number"
                          value={currentDpi.vertical}
                          onChange={(e) => {
                            if (isSecondary) {
                              setSecondaryDpi((prev) => ({
                                ...prev,
                                vertical: parseInt(e.target.value)
                              }))
                              setSelectedSecondaryPresetId('custom')
                            } else if (isTertiary) {
                              setTertiaryDpi((prev) => ({
                                ...prev,
                                vertical: parseInt(e.target.value)
                              }))
                              setSelectedTertiaryPresetId('custom')
                            } else {
                              setDpi((prev) => ({
                                ...prev,
                                vertical: parseInt(e.target.value)
                              }))
                              setSelectedPresetId('custom')
                            }
                          }}
                          title={t('printerSettings.dpi.vertical')}
                          className={numberClass}
                        />
                      </div>
                    </div>
                  </div>
                </div>
    )
    const positionShiftOpen = isSecondary
      ? showSecondaryPositionShift
      : isTertiary
        ? showTertiaryPositionShift
        : showPositionShift
    const togglePositionShift = (): void => {
      if (isSecondary) setShowSecondaryPositionShift(!showSecondaryPositionShift)
      else if (isTertiary) setShowTertiaryPositionShift(!showTertiaryPositionShift)
      else setShowPositionShift(!showPositionShift)
    }
    const positionShiftGrid = (
                  <div className={quadClass}>
                    <div className={pairCellClass}>
                      <label className={pairLabelClass}>
                        {t('printerSettings.positionShiftTop')}
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={currentPositionShift.top}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryPositionShift((prev) => ({
                                ...prev,
                                top: parseFloat(e.target.value) || 0
                              }))
                            : isTertiary
                              ? setTertiaryPositionShift((prev) => ({
                                  ...prev,
                                  top: parseFloat(e.target.value) || 0
                                }))
                              : setPositionShift((prev) => ({
                                  ...prev,
                                  top: parseFloat(e.target.value) || 0
                                }))
                        }
                        title={t('printerSettings.positionShiftTopTitle')}
                        className={numberClass}
                      />
                    </div>
                    <div className={pairCellClass}>
                      <label className={pairLabelClass}>
                        {t('printerSettings.positionShiftBottom')}
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={currentPositionShift.bottom}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryPositionShift((prev) => ({
                                ...prev,
                                bottom: parseFloat(e.target.value) ?? -0.5
                              }))
                            : isTertiary
                              ? setTertiaryPositionShift((prev) => ({
                                  ...prev,
                                  bottom: parseFloat(e.target.value) ?? -0.5
                                }))
                              : setPositionShift((prev) => ({
                                  ...prev,
                                  bottom: parseFloat(e.target.value) ?? -0.5
                                }))
                        }
                        title={t(
                          'printerSettings.positionShiftBottomTitle'
                        )}
                        className={numberClass}
                      />
                    </div>
                    <div className={pairCellClass}>
                      <label className={pairLabelClass}>
                        {t('printerSettings.positionShiftLeft')}
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={currentPositionShift.left}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryPositionShift((prev) => ({
                                ...prev,
                                left: parseFloat(e.target.value) || 0
                              }))
                            : isTertiary
                              ? setTertiaryPositionShift((prev) => ({
                                  ...prev,
                                  left: parseFloat(e.target.value) || 0
                                }))
                              : setPositionShift((prev) => ({
                                  ...prev,
                                  left: parseFloat(e.target.value) || 0
                                }))
                        }
                        title={t('printerSettings.positionShiftLeftTitle')}
                        className={numberClass}
                      />
                    </div>
                    <div className={pairCellClass}>
                      <label className={pairLabelClass}>
                        {t('printerSettings.positionShiftRight')}
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={currentPositionShift.right}
                        onChange={(e) =>
                          isSecondary
                            ? setSecondaryPositionShift((prev) => ({
                                ...prev,
                                right: parseFloat(e.target.value) || 0
                              }))
                            : isTertiary
                              ? setTertiaryPositionShift((prev) => ({
                                  ...prev,
                                  right: parseFloat(e.target.value) || 0
                                }))
                              : setPositionShift((prev) => ({
                                  ...prev,
                                  right: parseFloat(e.target.value) || 0
                                }))
                        }
                        title={t('printerSettings.positionShiftRightTitle')}
                        className={numberClass}
                      />
                    </div>
                  </div>
    )

    return (
      <div className={isPanel ? 'divide-y divide-gray-100' : 'space-y-6'}>
        {/* a) Printer Dropdown — shown on every shell. Electron and Tauri/Android fill
            `devices.printers`; a plain browser leaves it empty and the operator keeps
            "system default", which is what the browser print dialog uses anyway. */}
        <div>
          {/* In the panel the picker lives in the sticky strip beside the tabs (see
              `part === 'picker'`); the standalone modal keeps it here under a label. */}
          {!isPanel && (
            <label className="block text-sm font-medium mb-2 text-gray-700">
              {t('settings.printer.title')}
            </label>
          )}
          {!isPanel && slotPickerRow}
          {inHub && activeProjectId && !activeProjectIsHub && (
            <div className={isPanel ? 'mt-3' : 'mt-3 p-3 border border-gray-200 rounded-md'}>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="project-printer-binding"
                  checked={printerSlotByProject?.[activeProjectId] === printerType}
                  onChange={() => setPrinterSlotForProject(activeProjectId, printerType)}
                />
                {t(
                  'printerSettings.projectBinding.useForProject',
                  'Use this printer for the active project: {{title}}',
                  { title: activeProjectTitle || activeProjectId }
                )}
              </label>
              {printerSlotByProject?.[activeProjectId] === printerType && (
                <div className="mt-2 flex items-center gap-3 text-xs">
                  {!currentPrinter && (
                    <span className="text-amber-600">
                      {t(
                        'printerSettings.projectBinding.noPrinter',
                        'No printer is selected on this slot — bound prints fall back to frame/category/default routing.'
                      )}
                    </span>
                  )}
                  <button
                    type="button"
                    className="text-blue-600 underline"
                    onClick={() => setPrinterSlotForProject(activeProjectId, undefined)}
                  >
                    {t('printerSettings.projectBinding.unbind', 'Remove binding')}
                  </button>
                </div>
              )}
              <div className="mt-1 text-xs text-gray-500">
                {t(
                  'printerSettings.projectBinding.help',
                  'Frames and categories claimed by another printer still win; everything else from this project prints here.'
                )}
              </div>
            </div>
          )}

          {!isSecondary && !isTertiary && multiClaimRouting && hasAdditionalPrinters && (
            <div className={isPanel ? 'mt-3' : 'mt-3 p-3 border border-gray-200 rounded-md'}>
              <div className="text-sm font-medium mb-2">
                {t('printerSettings.routing.framesForThisPrinter', 'Frames for this printer')}
              </div>
              <label className="flex items-center gap-2 text-sm mb-1 cursor-pointer">
                <input
                  type="radio"
                  name="default-routing-mode"
                  checked={defaultRouting?.allFrames !== false}
                  onChange={() =>
                    setDefaultRouting({
                      allFrames: true,
                      frameIds: defaultRouting?.frameIds ?? [],
                      categories: defaultRouting?.categories ?? []
                    })
                  }
                />
                {t('printerSettings.routing.allFrames', 'All frames (default)')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="default-routing-mode"
                  checked={defaultRouting?.allFrames === false}
                  onChange={() =>
                    setDefaultRouting({
                      allFrames: false,
                      frameIds: defaultRouting?.frameIds ?? [],
                      categories: defaultRouting?.categories ?? []
                    })
                  }
                />
                {t(
                  'printerSettings.routing.onlySelected',
                  'Only selected frames and categories'
                )}
              </label>
              {defaultRouting?.allFrames === false && (
                <div className="mt-2 text-xs text-gray-500">
                  {t(
                    'printerSettings.routing.defaultSummary',
                    '{{frames}} frames, {{categories}} categories claimed. Unclaimed frames still print here as a fallback.',
                    {
                      frames: defaultRouting?.frameIds.length ?? 0,
                      categories: defaultRouting?.categories.length ?? 0
                    }
                  )}
                </div>
              )}
            </div>
          )}

          {loadingCapabilities && (
            <div className="mt-2 text-xs text-blue-600">
              {t('printerSettings.smartSetup.loading')}
            </div>
          )}

          {/* Android prints through the OS print sheet unless the bridge is given a
              RAW/JetDirect address, and an unattended booth has nobody to tap "Print".
              The native side has accepted `printerIp` all along; there was simply no
              field to type one into. Primary slot only — the Android bridge takes a
              single target. */}
          {capabilities.silentPrintNeedsIp && printerType === 'default' && (
            <div className={isPanel ? fieldClass : 'mt-3'}>
              <label className={fieldTitleClass} htmlFor="android-printer-ip">
                {t('printerSettings.androidPrinterIp.label', 'Printer IP (silent print)')}
              </label>
              <input
                id="android-printer-ip"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                spellCheck={false}
                placeholder="192.168.1.50"
                value={androidPrinterIp || ''}
                onChange={(e) => setAndroidPrinterIp(e.target.value)}
                className={isPanel ? `${controlPos} w-44 p-2 ${inputBase}` : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}
              />
              <p className={fieldHintClass}>
                {t(
                  'printerSettings.androidPrinterIp.explanation',
                  'Network address of the printer, for direct printing on port 9100. Leave empty to use the Android print dialog instead.'
                )}
              </p>
            </div>
          )}
        </div>

        {/* A browser tab can configure everything below; it just can't print silently.
            So this is an addendum to the settings now, not a replacement for them. */}
        {!isNativeShell && (
          <div className="mt-4 space-y-2">
            <a
              href={`${process.env.VITE_API_URL}/dashboard/installation`}
              className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload className="mr-1" />
              {t(
                'settings.printer.downloadDesktop',
                'Download desktop version for full printer support'
              )}
            </a>
          </div>
        )}

        {/* Smart printer presets (Electron only; Tauri shows legacy manual config below) */}
        {/* Hide presets when System Default (empty printer) + SumatraPDF enabled */}
        {hasPrinterCapsSupport &&
        !(currentUseSumatraPDF && !currentPrinter) &&
        !isHotFolder &&
        currentPrinter ? (
          <>
            {/* b) Combined Size/Orientation/DPI Dropdown - Always show when printer selected */}
            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('printerSettings.preset')}
              </label>
              <select
                value={currentPresetId}
                onChange={(e) => handlePresetChange(e.target.value, printerType)}
                className={selectClass}
                title={t('printerSettings.preset')}
              >
                {(() => {
                  const recommendation = currentPrinter
                    ? findPrinterRecommendation(currentPrinter)
                    : null
                  if (recommendation) {
                    const widthMm = PrintUtils.micronsToMm(
                      recommendation.settings.pageSize.width
                    )
                    const heightMm = PrintUtils.micronsToMm(
                      recommendation.settings.pageSize.height
                    )
                    const recDpi = getRecommendationDpi(recommendation)
                    return (
                      <option value={DREAMBOOTH_RECOMMENDED_PRESET_ID}>
                        ★ {t('printerSettings.recommended.label')} ({widthMm}×{heightMm}
                        mm) - {recommendation.settings.orientation} - {recDpi.horizontal}×
                        {recDpi.vertical} DPI
                      </option>
                    )
                  }
                  return null
                })()}
                {currentOptions.length > 0 ? (
                  currentOptions.map((opt, idx) => {
                    const recommendation = currentPrinter
                      ? findPrinterRecommendation(currentPrinter)
                      : null
                    const isRecommendedMatch =
                      recommendation != null && presetMatchesRecommendation(opt, recommendation)
                    const displayName =
                      opt.name && opt.name !== 'Unknown' ? opt.name : `#${idx + 1}`
                    const widthMm = PrintUtils.micronsToMm(opt.pageSize.width)
                    const heightMm = PrintUtils.micronsToMm(opt.pageSize.height)
                    const badge = isRecommendedMatch
                      ? ` ★ ${t('printerSettings.recommended.short')}`
                      : ''
                    return (
                      <option key={opt.id} value={opt.id}>
                        {displayName} ({widthMm}×{heightMm}mm) - {opt.orientation} -{' '}
                        {opt.dpi.horizontal}×{opt.dpi.vertical} DPI{badge}
                      </option>
                    )
                  })
                ) : (
                  <option value="custom">
                    {t('printerSettings.noCapabilities')}
                  </option>
                )}
                <option value="custom">{t('printerSettings.custom')}</option>
              </select>
              <p className={fieldHintClass}>
                {currentOptions.length > 0
                  ? t('printerSettings.presetHelp')
                  : t('printerSettings.noCapabilitiesHelp')}
              </p>
            </div>

            {/* Details Section - Right under preset dropdown. In the panel it is a flat
                disclosure like Advanced; the modal keeps its card and arrow button. */}
            {isPanel ? (
              currentOptions.length > 0 ? (
                <SettingsDisclosure
                  title={t('printerSettings.details')}
                  hint={t('printerSettings.detailsHint', 'Orientation, page size and DPI behind this preset')}
                  open={currentShowAdvanced}
                  onToggle={() => setCurrentShowAdvanced(!currentShowAdvanced)}
                >
                  {presetDetailsBody}
                </SettingsDisclosure>
              ) : (
                <div className="py-3">
                  <div className="text-sm text-gray-800">{t('printerSettings.details')}</div>
                  {currentShowAdvanced && <div className="mt-2">{presetDetailsBody}</div>}
                </div>
              )
            ) : (
            <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
              {currentOptions.length > 0 ? (
                <button
                  onClick={() => setCurrentShowAdvanced(!currentShowAdvanced)}
                  className={`w-full flex items-center justify-between text-sm font-medium text-gray-700 ${currentShowAdvanced ? 'mb-3' : 'mb-0'}`}
                  type="button"
                >
                  <span>{t('printerSettings.details')}</span>
                  <span>{currentShowAdvanced ? '▼' : '▶'}</span>
                </button>
              ) : (
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  {t('printerSettings.details')}
                </h3>
              )}

              {currentShowAdvanced && presetDetailsBody}
            </div>
            )}
          </>
        ) : (
          <>
            {/* Legacy manual configuration (for older Electron versions) */}
            {/* Hide when System Default + SumatraPDF enabled, or in hot-folder mode */}
            {!(currentUseSumatraPDF && !currentPrinter) && !isHotFolder && (
              <>
                {/* Orientation */}
                <div className={fieldClass}>
                  <label className={fieldTitleClass}>
                    {t('printerSettings.orientation')}
                  </label>
                  <select
                    title={t('printerSettings.orientation')}
                    value={currentSettings.orientation}
                    onChange={(e) =>
                      isSecondary
                        ? setTempSecondarySettings((prev) => ({
                            ...prev,
                            orientation: e.target.value as 'landscape' | 'portrait'
                          }))
                        : isTertiary
                          ? setTempTertiarySettings((prev) => ({
                              ...prev,
                              orientation: e.target.value as 'landscape' | 'portrait'
                            }))
                          : setTempSettings((prev) => ({
                              ...prev,
                              orientation: e.target.value as 'landscape' | 'portrait'
                            }))
                    }
                    className={selectClass}
                  >
                    <option value="landscape">
                      {t('printerSettings.landscape')}
                    </option>
                    <option value="portrait">
                      {t('printerSettings.portrait')}
                    </option>
                  </select>
                </div>

                {/* Page Size */}
                <div className={fieldClass}>
                  <label className={fieldTitleClass}>
                    {t('printerSettings.pageSize')}
                  </label>
                  <div className={pairClass}>
                    <div className={pairCellClass}>
                      <label className={pairLabelClass}>
                        {t('printerSettings.width')}
                      </label>
                      <input
                        type="number"
                        value={currentSettings.pageSize.width}
                        onChange={(e) =>
                          isSecondary
                            ? setTempSecondarySettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  width: parseInt(e.target.value)
                                }
                              }))
                            : isTertiary
                              ? setTempTertiarySettings((prev) => ({
                                  ...prev,
                                  pageSize: {
                                    ...prev.pageSize,
                                    width: parseInt(e.target.value)
                                  }
                                }))
                              : setTempSettings((prev) => ({
                                  ...prev,
                                  pageSize: {
                                    ...prev.pageSize,
                                    width: parseInt(e.target.value)
                                  }
                                }))
                        }
                        title={t('printerSettings.width')}
                        className={numberClass}
                      />
                    </div>
                    <div className={pairCellClass}>
                      <label className={pairLabelClass}>
                        {t('printerSettings.height')}
                      </label>
                      <input
                        type="number"
                        value={currentSettings.pageSize.height}
                        onChange={(e) =>
                          isSecondary
                            ? setTempSecondarySettings((prev) => ({
                                ...prev,
                                pageSize: {
                                  ...prev.pageSize,
                                  height: parseInt(e.target.value)
                                }
                              }))
                            : isTertiary
                              ? setTempTertiarySettings((prev) => ({
                                  ...prev,
                                  pageSize: {
                                    ...prev.pageSize,
                                    height: parseInt(e.target.value)
                                  }
                                }))
                              : setTempSettings((prev) => ({
                                  ...prev,
                                  pageSize: {
                                    ...prev.pageSize,
                                    height: parseInt(e.target.value)
                                  }
                                }))
                        }
                        title={t('printerSettings.height')}
                        className={numberClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Print Quality (DPI) - Hide when using printer driver defaults */}
                {!currentUseSumatraPDF && !isHotFolder && (
                  <div className={fieldClass}>
                    <label className={fieldTitleClass}>
                      {t('printerSettings.dpi')}
                    </label>
                    <div className={pairClass}>
                      <div className={pairCellClass}>
                        <label className={pairLabelClass}>
                          {t('printerSettings.dpi.horizontal')}
                        </label>
                        <input
                          type="number"
                          value={currentDpi.horizontal}
                          onChange={(e) =>
                            isSecondary
                              ? setSecondaryDpi((prev) => ({
                                  ...prev,
                                  horizontal: parseInt(e.target.value)
                                }))
                              : isTertiary
                                ? setTertiaryDpi((prev) => ({
                                    ...prev,
                                    horizontal: parseInt(e.target.value)
                                  }))
                                : setDpi((prev) => ({
                                    ...prev,
                                    horizontal: parseInt(e.target.value)
                                  }))
                          }
                          title={t('printerSettings.dpi.horizontal')}
                          className={numberClass}
                        />
                      </div>
                      <div className={pairCellClass}>
                        <label className={pairLabelClass}>
                          {t('printerSettings.dpi.vertical')}
                        </label>
                        <input
                          type="number"
                          value={currentDpi.vertical}
                          onChange={(e) =>
                            isSecondary
                              ? setSecondaryDpi((prev) => ({
                                  ...prev,
                                  vertical: parseInt(e.target.value)
                                }))
                              : isTertiary
                                ? setTertiaryDpi((prev) => ({
                                    ...prev,
                                    vertical: parseInt(e.target.value)
                                  }))
                                : setDpi((prev) => ({
                                    ...prev,
                                    vertical: parseInt(e.target.value)
                                  }))
                          }
                          title={t('printerSettings.dpi.vertical')}
                          className={numberClass}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Margins, scale, resolution, position, remote status and print method: the
            tier a booth on a preset never opens. Bare in the standalone modal. */}
        <SettingsDisclosure
          enabled={isPanel}
          title={t('settings.tiers.advancedPrinter', 'Advanced')}
          hint={t('settings.tiers.advancedPrinterHint', 'Margins, scale, resolution, position and print method')}
          open={showAdvancedTier[printerType]}
          onToggle={() =>
            setShowAdvancedTier((tiers) => ({ ...tiers, [printerType]: !tiers[printerType] }))
          }
        >
        {/* c) Margins Dropdown - Hide when using printer driver defaults */}
        {!currentUseSumatraPDF && !isHotFolder && (
          <div className={fieldClass}>
            <label className={fieldTitleClass}>
              {t('printerSettings.margins')}
            </label>
            <select
              value={currentMarginType}
              onChange={(e) =>
                isSecondary
                  ? setSecondaryMarginType(
                      e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                    )
                  : isTertiary
                    ? setTertiaryMarginType(
                        e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                      )
                    : setMarginType(
                        e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                      )
              }
              title={t('printerSettings.margins')}
              className={isPanel ? selectClass : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2'}
            >
              <option value="default">{t('printerSettings.margins.default')}</option>
              <option value="none">{t('printerSettings.margins.none')}</option>
              <option value="printableArea">
                {t('printerSettings.margins.printableArea')}
              </option>
              <option value="custom">{t('printerSettings.margins.custom')}</option>
            </select>

            {currentMarginType === 'custom' && (
              <div className={quadClass}>
                <div className={pairCellClass}>
                  <label className={pairLabelClass}>
                    {t('printerSettings.margins.top')}
                  </label>
                  <input
                    type="number"
                    value={currentMargins.top}
                    onChange={(e) =>
                      isSecondary
                        ? setSecondaryMargins((prev) => ({
                            ...prev,
                            top: parseInt(e.target.value)
                          }))
                        : isTertiary
                          ? setTertiaryMargins((prev) => ({
                              ...prev,
                              top: parseInt(e.target.value)
                            }))
                          : setMargins((prev) => ({ ...prev, top: parseInt(e.target.value) }))
                    }
                    title={t('printerSettings.margins.topTitle')}
                    className={numberClass}
                  />
                </div>
                <div className={pairCellClass}>
                  <label className={pairLabelClass}>
                    {t('printerSettings.margins.bottom')}
                  </label>
                  <input
                    type="number"
                    value={currentMargins.bottom}
                    onChange={(e) =>
                      isSecondary
                        ? setSecondaryMargins((prev) => ({
                            ...prev,
                            bottom: parseInt(e.target.value)
                          }))
                        : isTertiary
                          ? setTertiaryMargins((prev) => ({
                              ...prev,
                              bottom: parseInt(e.target.value)
                            }))
                          : setMargins((prev) => ({
                              ...prev,
                              bottom: parseInt(e.target.value)
                            }))
                    }
                    title={t('printerSettings.margins.bottomTitle')}
                    className={numberClass}
                  />
                </div>
                <div className={pairCellClass}>
                  <label className={pairLabelClass}>
                    {t('printerSettings.margins.left')}
                  </label>
                  <input
                    type="number"
                    value={currentMargins.left}
                    onChange={(e) =>
                      isSecondary
                        ? setSecondaryMargins((prev) => ({
                            ...prev,
                            left: parseInt(e.target.value)
                          }))
                        : isTertiary
                          ? setTertiaryMargins((prev) => ({
                              ...prev,
                              left: parseInt(e.target.value)
                            }))
                          : setMargins((prev) => ({ ...prev, left: parseInt(e.target.value) }))
                    }
                    title={t('printerSettings.margins.leftTitle')}
                    className={numberClass}
                  />
                </div>
                <div className={pairCellClass}>
                  <label className={pairLabelClass}>
                    {t('printerSettings.margins.right')}
                  </label>
                  <input
                    type="number"
                    value={currentMargins.right}
                    onChange={(e) =>
                      isSecondary
                        ? setSecondaryMargins((prev) => ({
                            ...prev,
                            right: parseInt(e.target.value)
                          }))
                        : isTertiary
                          ? setTertiaryMargins((prev) => ({
                              ...prev,
                              right: parseInt(e.target.value)
                            }))
                          : setMargins((prev) => ({ ...prev, right: parseInt(e.target.value) }))
                    }
                    title={t('printerSettings.margins.rightTitle')}
                    className={numberClass}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* d) Scale Factor */}
        {/* Scale Factor - Hide when using printer driver defaults */}
        {!currentUseSumatraPDF && !isHotFolder && (
          <div className={fieldClass}>
            <label className={fieldTitleClass}>
              {t('settings.printer.scaleFactor')}
            </label>
            <input
              type="number"
              min="10"
              max="200"
              step="1"
              value={currentScaleFactor}
              onChange={(e) =>
                isSecondary
                  ? setSecondaryScaleFactor(Math.round(Number(e.target.value)))
                  : isTertiary
                    ? setTertiaryScaleFactor(Math.round(Number(e.target.value)))
                    : setScaleFactor(Math.round(Number(e.target.value)))
              }
              title={t('settings.printer.scaleFactor')}
              className={soloNumberClass}
            />
            <span className={fieldHintClass}>
              {t('settings.printer.scaleFactorHelp')}
            </span>
          </div>
        )}

        {/* e) Print Background — a switch like every other boolean on the page; the
            standalone modal keeps its checkbox. */}
        <div className={isPanel ? fieldClass : 'flex items-center'}>
          {isPanel ? (
            <>
              <span className={fieldTitleClass}>{t('settings.printer.printBackground')}</span>
              <span className={fieldHintClass}>
                {t(
                  'settings.printer.printBackgroundHint',
                  'Include background colours and images in the print.'
                )}
              </span>
              <label className={`${controlPos} relative inline-flex cursor-pointer items-center`}>
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={currentPrintBackground}
                  onChange={(e) =>
                    isSecondary
                      ? setSecondaryPrintBackground(e.target.checked)
                      : isTertiary
                        ? setTertiaryPrintBackground(e.target.checked)
                        : setPrintBackground(e.target.checked)
                  }
                  aria-label={t('settings.printer.printBackground')}
                />
                <div className={printerToggleClass} />
              </label>
            </>
          ) : (
            <>
              <input
                type="checkbox"
                id={`printBackground${isSecondary ? '-secondary' : isTertiary ? '-tertiary' : ''}`}
                checked={currentPrintBackground}
                onChange={(e) =>
                  isSecondary
                    ? setSecondaryPrintBackground(e.target.checked)
                    : isTertiary
                      ? setTertiaryPrintBackground(e.target.checked)
                      : setPrintBackground(e.target.checked)
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`printBackground${isSecondary ? '-secondary' : isTertiary ? '-tertiary' : ''}`}
                className="ml-2 block text-sm text-gray-700"
              >
                {t('settings.printer.printBackground')}
              </label>
            </>
          )}
        </div>

        {/* f) Print Resolution Scale */}
        <div className={fieldClass}>
          <label className={fieldTitleClass}>
            {t('printerSettings.printResolutionScale.title')}
          </label>
          <select
            title={t('printerSettings.printResolutionScale.title')}
            value={currentPrintResolutionScale}
            onChange={(e) => setcurrentPrintResolutionScale(parseFloat(e.target.value))}
            className={selectClass}
          >
            <option value={0.5}>0.5x</option>
            <option value={1}>1x (Default)</option>
            <option value={2}>2x</option>
            <option value={3}>3x</option>
          </select>
          <p className={fieldHintClass}>
            {t(
              'printerSettings.printResolutionScale.description',
              'Adjust the resolution of the final printed image. Higher values result in better quality but larger file sizes.'
            )}
          </p>
        </div>

        {/* Print Position Shift - Collapsible - Only show for versions >= 2.0.2 */}
        {!currentUseSumatraPDF && !isHotFolder && isElectron && getPreloadApi() && supportsSumatraPDF && (
          isPanel ? (
            <SettingsDisclosure
              title={t('printerSettings.positionShift')}
              hint={t(
                'printerSettings.positionShiftHelp',
                'Fine-tune print alignment. Adjusts @page margins independently from margin settings.'
              )}
              open={positionShiftOpen}
              onToggle={togglePositionShift}
            >
              {positionShiftGrid}
            </SettingsDisclosure>
          ) : (
            <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
              <button
                onClick={togglePositionShift}
                className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-0"
                type="button"
              >
                <span>{t('printerSettings.positionShift')}</span>
                <span>{positionShiftOpen ? '▼' : '▶'}</span>
              </button>
              {positionShiftOpen && (
                <div className="mt-3 space-y-4">
                  <p className="text-xs text-gray-500">
                    {t(
                      'printerSettings.positionShiftHelp',
                      'Fine-tune print alignment. Adjusts @page margins independently from margin settings.'
                    )}
                  </p>
                  {positionShiftGrid}
                </div>
              )}
            </div>
          )
        )}

        {!isPanel && printerType === 'default' && <div className="mt-6">{remotePrinterStatusJsx}</div>}

        {!isPanel && printerType === 'default' && <div className="mt-6">{printResultMinDisplayJsx}</div>}

        {/* Print Method selector - Only show for versions >= 2.0.2 */}
        {supportsSumatraPDF && (
          <div className={isPanel ? `${fieldClass} mt-1` : 'mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md'}>
            <label htmlFor={`print-method-${printerType}`} className={fieldTitleClass}>
              {t('printerSettings.printMethod.title', 'Print method')}
            </label>
            <select
              id={`print-method-${printerType}`}
              value={currentPrintMethod}
              onChange={(e) => setPrintMethod(e.target.value as PrintMethod, methodSlot)}
              className={isPanel ? selectClass : 'mt-2 w-full p-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm'}
            >
              <option value="electron">
                {t('printerSettings.printMethod.electron', 'App Print')}
              </option>
              <option value="sumatra">
                {t('printerSettings.printMethod.sumatra', 'Default Driver')}
              </option>
              {supportsHotFolder && (
                <option value="hotFolder">
                  {t('printerSettings.printMethod.hotFolder', 'Hot Folder')}
                </option>
              )}
            </select>

            {/* Contextual help for the selected method */}
            <p className={fieldHintClass}>{printMethodHelp[currentPrintMethod]}</p>

            {/* Hot-folder configuration */}
            {isHotFolder && (
              <div className={isPanel ? 'col-span-2 mt-3 space-y-2 border-t border-gray-100 pt-3' : 'mt-3 space-y-2 border-t border-gray-200 pt-3'}>
                <label
                  htmlFor={`hot-folder-path-${printerType}`}
                  className="block text-xs font-medium text-gray-700"
                >
                  {t('printerSettings.printMethod.hotFolderPath', 'Watched folder')}
                </label>
                <div className="flex gap-2">
                  <input
                    id={`hot-folder-path-${printerType}`}
                    type="text"
                    value={currentHotFolderConfig?.folderPath ?? ''}
                    onChange={(e) =>
                      setHotFolderConfig({ folderPath: e.target.value }, methodSlot)
                    }
                    placeholder={t(
                      'printerSettings.printMethod.hotFolderPlaceholder',
                      'e.g. C:\\DNP\\HotFolder\\6x20'
                    )}
                    className="flex-1 p-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                  />
                  <Button
                    text={t('printerSettings.printMethod.browse', 'Browse')}
                    variant="secondary"
                    onClick={async () => {
                      const fs = getPreloadApi()
                      if (!fs?.showOpenDirectoryDialog) return
                      const picked = await fs.showOpenDirectoryDialog()
                      if (picked) {
                        setHotFolderConfig({ folderPath: picked }, methodSlot)
                      }
                    }}
                    className="!px-3 !py-1 !text-xs whitespace-nowrap"
                  />
                </div>
                {!currentHotFolderConfig?.folderPath?.trim() && (
                  <p className="text-[11px] text-amber-600">
                    {t(
                      'printerSettings.printMethod.hotFolderRequired',
                      'Select a watched folder, otherwise prints with this method will fail.'
                    )}
                  </p>
                )}
                <p className="text-[11px] text-gray-500">
                  {t(
                    'printerSettings.printMethod.hotFolderHelp',
                    'Point this at a folder monitored by your print software (e.g. DNP Hot Folder Print), mapped to the desired output size. The app drops one image file per copy; the print software handles the size and cutting.'
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* i) C# Printing Toggle (only on default tab) */}
        {printerType === 'default' && isElectron && process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-blue-800">
                  {t(
                    'printerSettings.csharpPrinting.title',
                    'C# Direct Printing (Experimental)'
                  )}
                </h4>
                <p className="text-xs text-blue-600 mt-1">
                  {t(
                    'printerSettings.csharpPrinting.description',
                    'Use .NET printing for better reliability with certain printers'
                  )}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCSharpPrinting}
                  onChange={(e) => setUseCSharpPrinting(e.target.checked)}
                  className="sr-only peer"
                  aria-label={t('printerSettings.csharpPrinting.toggle')}
                />
                <div className={printerToggleClass} />
              </label>
            </div>
          </div>
        )}
        </SettingsDisclosure>
      </div>
    )
  }

  const mobileSheet = capabilities.isMobile || isViewportCompact

  /**
   * Booth-level, not per-slot: this booth as a remote print target, and how long the
   * result screen holds. Inline they sit with the general settings above the slot
   * tabs; the standalone modal keeps them where they were, in the default tab.
   */
  const remotePrinterStatusJsx = (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium mb-2 text-gray-700">
              {t('printerSettings.remotePrinterStatus.title')}
            </h3>
            {isTunnelActive ? (
              <div>
                <p className="text-sm text-gray-700">
                  {t('printerSettings.remotePrinterStatus.active')}
                </p>
                {tunnelUrl && (
                  <p className="text-xs text-gray-600 mt-1">
                    {t('printerSettings.remotePrinterStatus.name')}
                    <span className="font-semibold">
                      {tunnelUrl.match(/https:\/\/([a-zA-Z0-9-]+)\.trycloudflare\.com/)?.[1] ||
                        'N/A'}
                    </span>
                  </p>
                )}
                <p className="text-xs text-blue-700 mt-2">
                  {t(
                    'printerSettings.remotePrinterStatus.description',
                    'The remote printer is active. Go to {{appUrl}}/strip to print remotely.',
                    { appUrl: process.env.VITE_APP_URL || 'your app URL' }
                  )}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {t(
                  'printerSettings.remotePrinterStatus.inactive',
                  'Status: Inactive or not connected.'
                )}
              </p>
            )}
          </div>
  )
  const printResultMinDisplayJsx = (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium mb-1 text-gray-800">
              {t('printerSettings.printResultMinDisplay.title')}
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              {t('printerSettings.printResultMinDisplay.description')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={0}
                max={PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS}
                value={printResultMinDisplaySeconds}
                onChange={(e) =>
                  setPrintResultMinDisplaySeconds(parseInt(e.target.value, 10) || 0)
                }
                className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label={t('printerSettings.printResultMinDisplay.title')}
              />
              <span className="text-xs text-gray-600">
                {t('printerSettings.printResultMinDisplay.suffix', {
                  max: PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS
                })}
              </span>
            </div>
          </div>
  )

  /**
   * The same two booth-level facts as single rows, for the panel: one line each,
   * status as a pill, the number inline. The cards above stay for the modal.
   */
  const remoteTunnelName =
    tunnelUrl?.match(/https:\/\/([a-zA-Z0-9-]+)\.trycloudflare\.com/)?.[1] ?? tunnelUrl ?? ''
  const remotePrinterRowJsx = (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-700">
          {t('printerSettings.remotePrinterStatus.rowTitle', 'Remote printer')}
        </div>
        <div className="text-xs text-gray-500">
          {isTunnelActive
            ? t(
                'printerSettings.remotePrinterStatus.activeHint',
                'Reachable as {{name}} — print to it from {{appUrl}}/strip',
                { name: remoteTunnelName, appUrl: process.env.VITE_APP_URL || 'your app URL' }
              )
            : t(
                'printerSettings.remotePrinterStatus.inactiveHint',
                'Not reachable as a remote printer right now.'
              )}
        </div>
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          isTunnelActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {isTunnelActive
          ? t('printerSettings.remotePrinterStatus.stateActive', 'Active')
          : t('printerSettings.remotePrinterStatus.stateInactive', 'Not connected')}
      </span>
    </div>
  )
  const printResultMinDisplayRowJsx = (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-700">
          {t('printerSettings.printResultMinDisplay.shortTitle', 'Hold the processing screen')}
        </div>
        <div className="text-xs text-gray-500">
          {t(
            'printerSettings.printResultMinDisplay.shortHint',
            'Seconds to keep it up after print and save finish, before Result. 0 = as soon as work completes.'
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <input
          type="number"
          min={0}
          max={PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS}
          value={printResultMinDisplaySeconds}
          onChange={(e) => setPrintResultMinDisplaySeconds(parseInt(e.target.value, 10) || 0)}
          className="w-16 rounded-md border border-gray-300 p-1.5 text-center text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          aria-label={t('printerSettings.printResultMinDisplay.title')}
        />
        <span className="text-xs text-gray-500">
          {t('printerSettings.printResultMinDisplay.unit', 's')}
        </span>
      </div>
    </div>
  )

  const renderPurpose = (slot: 'secondary' | 'tertiary'): JSX.Element => (
              <div className={isPanel ? fieldClass : 'mb-6'}>
                <label className={fieldTitleClass}>
                  {t('printerSettings.printerPurpose')}
                </label>
                <select
                  value={slot === 'secondary' ? secondaryPurpose : tertiaryPurpose}
                  onChange={(e) => {
                    const purpose = e.target.value as 'special-frames' | 'parallel'
                    if (slot === 'secondary') {
                      setSecondaryPurpose(purpose)
                    } else {
                      setTertiaryPurpose(purpose)
                    }
                  }}
                  className={selectClass}
                  title={t('printerSettings.printerPurpose')}
                >
                  <option value="special-frames">
                    {t('printerSettings.specialFramesPrint')}
                  </option>
                  <option value="parallel">
                    {t('printerSettings.parallelPrinting')}
                  </option>
                </select>
                <p className={fieldHintClass}>
                  {slot === 'secondary'
                    ? t(
                        'printerSettings.printerPurposeHelpSecondary',
                        'Select the purpose for the secondary printer: special frames for frame-based routing, or parallel for round-robin distribution.'
                      )
                    : t(
                        'printerSettings.printerPurposeHelpTertiary',
                        'Select the purpose for the tertiary printer: special frames for frame-based routing, or parallel for round-robin distribution.'
                      )}
                </p>
              </div>
  )

  /**
   * Panel mode moves the preview and the print actions out of the page and into a
   * sheet behind "Test print". They are verification, not settings — the one
   * place a second layer is right, because it produces an artefact rather than
   * holding a value the operator is looking for.
   */
  const previewColumnJsx = (
    <>
          {/* Preview Panel — larger share of height on portrait mobile sheet */}
          <div
            className={
              isPanel
                ? 'flex w-full flex-col'
                : `flex h-full min-h-0 w-full flex-1 flex-col overflow-y-auto border-t border-gray-200 md:w-1/2 md:flex-none md:border-t-0 md:border-l ${
                    mobileSheet ? 'min-h-[50vh]' : ''
                  }`
            }
          >
            <div className={printerSheetDense ? 'flex min-h-0 flex-1 flex-col p-2.5' : isPanel ? 'flex flex-col' : mobileSheet ? 'flex min-h-0 flex-1 flex-col p-3 md:p-6' : 'p-8'}>
              <h3 className={`font-medium ${printerSheetDense ? 'mb-1.5 text-sm' : mobileSheet ? 'mb-2 text-base md:mb-4 md:text-lg' : 'mb-4 text-lg'}`}>
                {t('printerSettings.preview')}
              </h3>
              <div
                className={`${isPanel ? 'rounded-lg bg-gray-50 p-2' : 'border border-gray-200 rounded-lg bg-gray-50 p-2 md:p-4'} ${
                  mobileSheet
                    ? 'min-h-[220px] flex-1 h-[min(52vh,480px)] md:h-[calc(60vh-8rem)] md:min-h-0 md:flex-none'
                    : isPanel
                      ? 'h-64'
                      : 'h-[calc(60vh-8rem)]'
                }`}
              >
                {previewImage ? (
                  <img
                    crossOrigin="anonymous"
                    src={previewImage}
                    alt="Print Preview"
                    className="object-contain h-full w-full"
                    style={{
                      aspectRatio: `${
                        activeTab === 'default'
                          ? tempSettings.pageSize.width
                          : activeTab === 'secondary'
                            ? tempSecondarySettings.pageSize.width
                            : tempTertiarySettings.pageSize.width
                      } / ${
                        activeTab === 'default'
                          ? tempSettings.pageSize.height
                          : activeTab === 'secondary'
                            ? tempSecondarySettings.pageSize.height
                            : tempTertiarySettings.pageSize.height
                      }`
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                    {t('printerSettings.noPreview')}
                  </div>
                )}
              </div>

              {/* Frame Test Section */}
              <div className={printerSheetDense ? 'mt-3' : mobileSheet ? 'mt-4 md:mt-8' : 'mt-8'}>
                <h3
                  className={`font-medium ${printerSheetDense ? 'mb-2 text-sm' : mobileSheet ? 'mb-2 text-base md:mb-4 md:text-lg' : 'mb-4 text-lg'}`}
                >
                  {t('printerSettings.frameTest.title')}
                </h3>
                <div className={printerSheetDense ? 'space-y-2' : 'space-y-4'}>
                  {/* Frame Selection Dropdown */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      {t('frame.framesListbox')}
                    </label>
                    <select
                      value={selectedFrameId}
                      onChange={(e) => setSelectedFrameId(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      title={t('frame.framesListbox')}
                    >
                      <option value="">{t('frame.framesListbox')}</option>
                      {frames.map((frame) => (
                        <option key={frame._id} value={frame._id}>
                          {frame.name || `Frame ${frame._id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">{t('common.or')}</span>
                    </div>
                  </div>

                  {/* Upload Section */}
                  <div
                    className={`border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                      printerSheetDense ? 'p-4' : mobileSheet ? 'p-5 md:p-8' : 'p-8'
                    } ${
                      isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-500'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => document.getElementById('frame-upload')?.click()}
                  >
                    <input
                      id="frame-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e): void => {
                        const file = e.target.files?.[0]
                        if (file) {
                          handleFileUpload(file)
                          setSelectedFrameId('') // Clear frame selection when uploading
                        }
                      }}
                      className="hidden"
                      title={t('printerSettings.upload')}
                    />
                    <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      {t(
                        'printerSettings.frameTest.dragAndDrop',
                        'Drag and drop an image here, or click to select'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
    </>
  )
  /**
   * Panel column: preview, then the print action right under it, then the test
   * source on one row. The standalone modal's column (with its headings and the
   * big drop zone) had the button below the fold.
   */
  const panelActiveSettings =
    activeTab === 'default'
      ? tempSettings
      : activeTab === 'secondary'
        ? tempSecondarySettings
        : tempTertiarySettings
  const panelPreviewColumnJsx = (
    <div className="flex w-full flex-col gap-3">
      <div
        className={`h-64 rounded-lg bg-gray-50 p-2 transition-shadow ${
          isDragging ? 'ring-2 ring-blue-400' : ''
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {previewImage ? (
          <img
            crossOrigin="anonymous"
            src={previewImage}
            alt="Print Preview"
            className="h-full w-full object-contain"
            style={{
              aspectRatio: `${panelActiveSettings.pageSize.width} / ${panelActiveSettings.pageSize.height}`
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-100 text-sm text-gray-400">
            {t('printerSettings.noPreview')}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isNativeShell && (
          <label className="flex shrink-0 items-center gap-1.5 text-xs text-gray-500">
            {t('printerSettings.copies')}
            <input
              type="number"
              min="1"
              value={copies}
              onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-14 rounded-md border border-gray-300 p-1.5 text-center text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              title={t('printerSettings.copiesTitle')}
            />
          </label>
        )}
        <Button
          text={
            <>
              <FaPrint className="mr-2 inline" />
              {t('printerSettings.preview')}
            </>
          }
          onClick={handlePreview}
          className="!flex-1 !px-4 !py-2 !text-sm"
          disabled={
            !previewImage ||
            (isNativeShell && activeTab === 'default' && !selectedPrinter) ||
            (isNativeShell && activeTab === 'secondary' && !selectedSecondaryPrinter) ||
            (isNativeShell && activeTab === 'tertiary' && !selectedTertiaryPrinter)
          }
        />
      </div>
      <select
        value={selectedFrameId}
        onChange={(e) => setSelectedFrameId(e.target.value)}
        className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        title={t('frame.framesListbox')}
        aria-label={t('frame.framesListbox')}
      >
        <option value="">{t('frame.framesListbox')}</option>
        {frames.map((frame) => (
          <option key={frame._id} value={frame._id}>
            {frame.name || `Frame ${frame._id}`}
          </option>
        ))}
      </select>
      {/* A slim drop strip: the affordance stays visible without the old 130px zone.
          The preview box above accepts drops too. */}
      <div
        role="button"
        tabIndex={0}
        className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50 text-blue-700'
            : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById('frame-upload')?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            document.getElementById('frame-upload')?.click()
          }
        }}
      >
        <FaUpload className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">
          {t('printerSettings.frameTest.dragAndDrop', 'Drag and drop an image here, or click to select')}
        </span>
      </div>
      <input
        id="frame-upload"
        type="file"
        accept="image/*"
        onChange={(e): void => {
          const file = e.target.files?.[0]
          if (file) {
            handleFileUpload(file)
            setSelectedFrameId('')
          }
        }}
        className="hidden"
        title={t('printerSettings.upload')}
      />
    </div>
  )

  const footerJsx = (
    <>
        <div
          className={
            isPanel
              ? 'mt-3'
              : `shrink-0 border-t border-gray-200 bg-white ${printerSheetDense ? 'px-2.5 py-2' : mobileSheet ? 'px-3 py-3' : 'rounded-b-2xl p-4'}`
          }
        >
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            {!isPanel && (
            <Button
              text={t('common.cancel')}
              variant="secondary"
              onClick={onClose}
              className={printerSheetDense || mobileSheet ? '!px-3 !py-1.5 !text-xs' : '!px-6 !py-2 !text-base'}
            />
            )}
            {isNativeShell && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">
                  {t('printerSettings.copies')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  title={t('printerSettings.copiesTitle')}
                />
              </div>
            )}
            <Button
              text={
                <>
                  <FaPrint className="mr-1 inline sm:mr-2" />
                  {t('printerSettings.preview')}
                </>
              }
              variant="secondary"
              onClick={handlePreview}
              className={mobileSheet ? '!px-3 !py-1.5 !text-xs' : '!px-6 !py-2 !text-base'}
              disabled={
                !previewImage ||
                (isNativeShell && activeTab === 'default' && !selectedPrinter) ||
                (isNativeShell && activeTab === 'secondary' && !selectedSecondaryPrinter) ||
                (isNativeShell && activeTab === 'tertiary' && !selectedTertiaryPrinter)
              }
            />
            {!isPanel && (
            <Button
              text={t('common.save')}
              onClick={() => {
                saveAllTabs()
                onClose()
              }}
              className={mobileSheet ? '!px-3 !py-1.5 !text-xs' : '!px-6 !py-2 !text-base'}
            />
            )}
          </div>
        </div>
    </>
  )

  return (
    <div
      className={
        isPanel
          ? 'block'
          : `fixed inset-0 z-[200] bg-black/50 ${
              mobileSheet
                ? 'flex flex-col justify-end p-0'
                : 'flex items-center justify-center p-4 sm:p-6'
            }`
      }
    >
      <div
        className={
          isPanel
            ? 'block w-full'
            : `flex w-full flex-col bg-white shadow-2xl ${
                mobileSheet
                  ? 'mx-0 h-[95dvh] max-h-[95dvh] rounded-t-2xl'
                  : 'mx-auto max-w-6xl rounded-2xl h-[90vh]'
              }`
        }
      >
        <div
          className={
            isPanel
              ? twoColumn
                ? 'grid grid-cols-[minmax(0,32rem)_minmax(16rem,1fr)] items-start gap-6'
                : 'flex flex-col gap-4'
              : 'flex h-full min-h-0 flex-col md:flex-row'
          }
        >
          {/* Settings Panel */}
          <div
            className={
              isPanel
                ? 'min-w-0 w-full'
                : `w-full overflow-y-auto md:w-1/2 md:min-h-0 ${
                    mobileSheet ? 'max-h-[36vh] shrink-0 border-b border-gray-200 md:max-h-none md:shrink md:border-b-0' : 'min-h-0'
                  } ${printerSheetDense ? 'p-2.5 [&_label]:mb-0.5 [&_label]:text-[11px] [&_input]:py-1.5 [&_input]:text-xs [&_select]:py-1.5 [&_select]:text-xs [&_.space-y-6]:!space-y-3 [&_.mb-6]:!mb-3 [&_.text-sm]:!text-xs' : mobileSheet ? 'p-3 md:p-4' : 'p-4 md:p-8'}`
            }
          >
            {isPanel && leading && <div className="mb-6">{leading}</div>}
            {!isPanel && (
              <h2
                className={`font-bold ${printerSheetDense ? 'mb-2 text-base' : mobileSheet ? 'mb-3 text-lg md:mb-6 md:text-xl' : 'mb-4 text-2xl md:mb-6'}`}
              >
                {t('settings.printer.settings')}
              </h2>
            )}

            {/* Panel: tabs + the active slot's picker stay pinned while the settings
                scroll, so a slot or printer switch never loses the scroll position. */}
            <div className={isPanel ? 'sticky top-0 z-20 border-b border-gray-100 bg-white pb-3' : undefined}>
            {/* Tabs */}
            <div className={`custom-scrollbar flex overflow-x-auto border-b border-gray-200 ${printerSheetDense ? 'mb-3' : isPanel ? 'mb-4' : 'mb-6'}`}>
              <button
                type="button"
                onClick={() => setActiveTab('default')}
                className={`shrink-0 whitespace-nowrap font-medium ${printerSheetDense ? 'px-2 py-1 text-[11px]' : 'px-4 py-2 text-sm'} ${
                  activeTab === 'default'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('printerSettings.defaultPrinter')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('secondary')}
                className={`shrink-0 whitespace-nowrap font-medium ${printerSheetDense ? 'px-2 py-1 text-[11px]' : 'px-4 py-2 text-sm'} ${
                  activeTab === 'secondary'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('printerSettings.secondaryPrinter')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('tertiary')}
                className={`shrink-0 whitespace-nowrap font-medium ${printerSheetDense ? 'px-2 py-1 text-[11px]' : 'px-4 py-2 text-sm'} ${
                  activeTab === 'tertiary'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('printerSettings.tertiaryPrinter')}
              </button>
            </div>
            {isPanel && renderPrinterSettings(activeTab, 'picker')}
            </div>

            {/* Printer Purpose Dropdown - Only for secondary and tertiary */}
            {(activeTab === 'secondary' || activeTab === 'tertiary') && renderPurpose(activeTab)}

            {!isPanel && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {t(
                      activeTab === 'default'
                        ? 'printerSettings.testPrintInfo.title'
                        : activeTab === 'secondary'
                          ? 'printerSettings.secondaryPrinterInfo.title'
                          : 'printerSettings.tertiaryPrinterInfo.title',
                      activeTab === 'default'
                        ? 'Test Print Configuration'
                        : activeTab === 'secondary'
                          ? 'Secondary Printer Configuration'
                          : 'Tertiary Printer Configuration'
                    )}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      {t(
                        activeTab === 'default'
                          ? 'printerSettings.testPrintInfo.description'
                          : activeTab === 'secondary'
                            ? 'printerSettings.secondaryPrinterInfo.description'
                            : 'printerSettings.tertiaryPrinterInfo.description',
                        activeTab === 'default'
                          ? 'Use this section to test print settings and save your preferred configuration. These settings will be used for automated printing during photo sessions.'
                          : activeTab === 'secondary'
                            ? 'Configure a secondary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
                            : 'Configure a tertiary printer for specific frames. You can select which frames will be printed using this printer. This is useful for printing different frame types on different printers.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {renderPrinterSettings(activeTab)}
            {/* Booth-level, not per-slot; parked under the slots so the picker and the
                slot's own settings come first. */}
            {isPanel && (
              <div className="mt-8 divide-y divide-gray-100 border-t border-gray-200 pt-2">
                {remotePrinterRowJsx}
                {printResultMinDisplayRowJsx}
              </div>
            )}
          </div>

          {isPanel ? (
            <div className={twoColumn ? 'sticky top-4 self-start' : ''}>
              {panelPreviewColumnJsx}
              {aside}
            </div>
          ) : (
            previewColumnJsx
          )}
        </div>
        {!isPanel && footerJsx}
      </div>

      {/* Frame Selection Modal */}
      {/* Key prop ensures complete remount when switching between secondary and tertiary */}
      <FrameSelectionModal
        key={`frame-selection-${frameSelectionPrinterType}`}
        isOpen={isFrameSelectionOpen}
        onClose={() => setIsFrameSelectionOpen(false)}
        selectedFrames={
          frameSelectionPrinterType === 'default'
            ? [...(defaultRouting?.frameIds || [])]
            : frameSelectionPrinterType === 'secondary'
              ? [...(secondaryPrinter?.frameIds || [])]
              : [...(tertiaryPrinter?.frameIds || [])]
        }
        selectedCategories={
          frameSelectionPrinterType === 'default'
            ? defaultRouting?.categories ?? []
            : frameSelectionPrinterType === 'secondary'
              ? secondaryPrinter?.routing?.categories ?? []
              : tertiaryPrinter?.routing?.categories ?? []
        }
        onSelectionChange={(frameIds, categories) => {
          if (frameSelectionPrinterType === 'default') {
            setDefaultRouting({
              allFrames: defaultRouting?.allFrames ?? true,
              frameIds: [...frameIds],
              categories
            })
            return
          }
          // Frame ids go through the legacy writeback below (which also mirrors
          // them into routing); categories have no legacy field, so store them
          // directly on the slot's routing.
          setSlotCategories(frameSelectionPrinterType, categories)
        }}
        onFramesSelected={(frameIds) => {
          if (frameSelectionPrinterType === 'default') return // handled above
          // Create a new array to ensure we don't share references
          const newFrameIds = [...frameIds]

          if (frameSelectionPrinterType === 'secondary') {
            setSecondaryPrinter({
              printerName: selectedSecondaryPrinter || secondaryPrinter?.printerName || '',
              settings: secondaryPrinter?.settings || {
                ...tempSecondarySettings,
                margins: {
                  marginType: secondaryMarginType,
                  ...(secondaryMarginType === 'custom' ? secondaryMargins : {})
                },
                dpi: secondaryDpi,
                scaleFactor: secondaryScaleFactor,
                printBackground: secondaryPrintBackground,
                printResolutionScale: secondaryPrintResolutionScale,
                printPositionShift: secondaryPositionShift
              },
              frameIds: newFrameIds,
              purpose: secondaryPurpose
            })
          } else {
            setTertiaryPrinter({
              printerName: selectedTertiaryPrinter || tertiaryPrinter?.printerName || '',
              settings: tertiaryPrinter?.settings || {
                ...tempTertiarySettings,
                margins: {
                  marginType: tertiaryMarginType,
                  ...(tertiaryMarginType === 'custom' ? tertiaryMargins : {})
                },
                dpi: tertiaryDpi,
                scaleFactor: tertiaryScaleFactor,
                printBackground: tertiaryPrintBackground,
                printResolutionScale: tertiaryPrintResolutionScale,
                printPositionShift: tertiaryPositionShift
              },
              frameIds: newFrameIds,
              purpose: tertiaryPurpose
            })
          }
        }}
        printerType={frameSelectionPrinterType}
      />
    </div>
  )
}
